// Disruptions view types per docs/domains/disruption.tds.md §5/§6 — the
// root-cause aggregate surface. The operator-ui reads these via the operator-bff
// proxy (`/v1/disruptions/*`); rollups are denormalized server-side (never
// computed client-side per §5).

import type { TripView } from '@/lib/transport/types';
import type { FlightProviderAttempt } from '@/lib/rebooking/types';
import type { FlightDataSource } from '@/lib/flights/types';

export type DisruptionStatus = 'DETECTED' | 'CONTAINED' | 'RESOLVED' | 'MERGED';
export type DisruptionCause =
  | 'WEATHER'
  | 'MECHANICAL'
  | 'STRIKE'
  | 'ATC'
  | 'SECURITY'
  | 'IT_OUTAGE'
  | 'OTHER';

export interface DisruptionScope {
  airports: string[];
  window?: { fromUtc?: string; toUtc?: string };
  flightUrns?: string[];
}

export interface DisruptionRollup {
  caseCount: number;
  affectedPassengers: number;
  rebookedPassengers: number;
  hotelsIssued: number;
  /**
   * `airline-money@v2` (commercial-contract M3): the hotel spend AS THE AIRLINE PAYS IT —
   * booked + held (real reservations) + est. for hotel groups still without one, each
   * priced server-side. `null` = not available («No disponible»: no price card, or booking
   * unreadable on the detail). ABSENT (`undefined`) on the list / overview rows: their
   * only money is the S4 estimate (mixed hotel + meals + flights, no split), which is
   * structurally unpriceable — the row carries no amount and the board shows "—".
   */
  totalPriceMinor?: number | null;
  currency: string;
  bookedPriceMinor?: number | null;
  heldPriceMinor?: number | null;
  estimatedPriceMinor?: number | null;
  priceSource?: 'actual+estimate' | 'estimate';
  /** false ⇒ every amount above is null. Optional: an older BFF omits it. */
  priced?: boolean;
}

/** SLA/staleness states (disruption.tds.md §3.3) the reconciler can flag. */
export type DisruptionStalenessState =
  | 'WINDOW_EXPIRED'
  | 'CLOSABLE'
  | 'PREDICTED_EXPIRED'
  | 'STALE_NO_CASES'
  | 'STALE_UNWORKED'
  | 'STALE_STALLED'
  | 'OK';

export type StalenessSeverity = 'none' | 'info' | 'warning' | 'critical';

/** SLA/staleness marker (§3.3) — present once the reconciler flags a decay. */
export interface DisruptionStaleness {
  state: DisruptionStalenessState;
  severity: StalenessSeverity;
  ageHours: number;
  reason: string;
  recommendedAction: 'NONE' | 'ESCALATE' | 'CONTAIN' | 'CLOSE' | 'EXPIRE';
  markedAt: string;
  actionTaken?: string;
}

/** The flight kinds of the signal severity lattice (DELAY < DIVERTED < CANCELLED). */
export type DisruptionFlightKind = 'DELAY' | 'CANCELLED' | 'DIVERTED';

/**
 * `operator-bff:IDisruptionListRow.flightStatus@v1` (flight-delay B1, brief §8b) — what
 * happened to the disruption's flights: `kind` (null when nothing is known), plus the
 * delay summary's worst delay, whether a delayed flight escalated to a cancellation and
 * the earliest open grace window. `maxDelayMinutes` / `escalated` / `graceUntil` are
 * ABSENT (never 0/false invented) when the BFF had no delay summary for the row.
 * Kept live over the WS by `lib/delay/merge.ts` (FlightDelayChanged / FlightEscalated).
 */
export interface IDisruptionFlightStatus {
  kind: DisruptionFlightKind | null;
  maxDelayMinutes?: number;
  escalated?: boolean;
  graceUntil?: string;
  /** A flight's provider report exceeds the policy maximum and waits for the operator
   *  (`operator-bff:IDisruptionFlightStatus.outOfPolicy@v1`, delay-limits). Unlike the
   *  rest it can turn OFF (dismissed, an in-policy report, escalation, departure). */
  outOfPolicy?: true;
}

export interface DisruptionListRow {
  urn: string;
  status: DisruptionStatus;
  cause: DisruptionCause;
  title: string;
  scope: DisruptionScope;
  rollup: DisruptionRollup;
  detectedAt: string;
  /** SLA/staleness marker (§3.3) — absent on healthy disruptions. */
  staleness?: DisruptionStaleness;
  /** The disruption's worst signal kind (escalate-in-place, monotonic), passed through
   *  by the BFF (flight-delay invariant 10). Absent on a hand-declared disruption. */
  worstEventKind?: DisruptionFlightKind;
  /** `flightStatus@v1` — on every list / overview / detail row of a current BFF;
   *  optional so an older BFF (and fixtures) stay valid — absent ⇒ no badge. */
  flightStatus?: IDisruptionFlightStatus;
}

/** Board tier per overview row, worst → quietest (ranked server-side). */
export type DisruptionSeverity = 'critical' | 'warning' | 'active' | 'settled';

/** One row of the network-situation board (GET /v1/disruptions/overview). */
export interface DisruptionOverviewRow extends DisruptionListRow {
  severity: DisruptionSeverity;
  /** affected − rebooked, floored at 0 — the pax still owed an outcome. */
  unresolvedPassengers: number;
}

export interface DisruptionOverview {
  /** Every OPEN disruption, ranked worst first by the BFF. */
  rows: DisruptionOverviewRow[];
  /** True when the BFF's page walk hit its cap — counts are a floor. */
  truncated: boolean;
}

export interface DisruptionCaseRow {
  caseUrn: string;
  airportCode: string;
  status: string;
  passengerCount: number;
  /** The affected flight's URN — the value the roster flight filter narrows on. */
  flightUrn?: string;
  /** The affected flight (a case ≈ a flight): number + route + date. */
  flightNumber?: string;
  origin?: string;
  destination?: string;
  departureAt?: string;
}

/** HOW a disruption was contained (`operator-bff:IDisruptionDetail.containment@v1`,
 *  additive): NATURAL / FORCED, who (`actor` URN), why, what was released, the facts. */
export interface DisruptionContainment {
  mode: 'NATURAL' | 'FORCED';
  actor?: string;
  /** The actor's display name, stamped at contain time (absent on sweeps / older rows). */
  actorName?: string;
  reason?: string;
  released?: { flights: number; hotels: number };
  facts?: {
    groups: number;
    travelled: number;
    declined: number;
    excluded: number;
    notApplicable: number;
    ticketed: number;
    open: number;
  };
}

/** What opened a disruption (operator-bff `DisruptionTriggerSource`): an operator by hand,
 *  a manifest file, or Nexa on a signal (airline status, weather, prediction). */
export type DisruptionTriggerSource = 'AUTO_WEATHER' | 'AUTO_AIRLINE' | 'AUTO_PREDICTOR' | 'MANUAL_OCC' | 'MANUAL_FILE';

export interface DisruptionDetail extends DisruptionListRow {
  description?: string;
  containedAt?: string;
  resolvedAt?: string;
  /** Absent until contained (or on an older BFF). */
  containment?: DisruptionContainment;
  /**
   * `operator-bff:IDisruptionDetail.openedBy@v1` — who declared it: the operator's URN and
   * the display name stamped beside it at declare time. Both absent on an automated open
   * and on a declare nobody was recorded for (`lib/disruptions/opener.ts` says which).
   */
  openedBy?: string;
  openedByName?: string;
  /** What opened it. Absent only on an older BFF. */
  triggerSource?: DisruptionTriggerSource;
  /** The manifest import a FILE declare came from. */
  provenance?: { kind: 'FILE_IMPORT'; importUrn: string };
  cases: DisruptionCaseRow[];
}

/** Triage view (operator "no ruido"): `active` (default) hides past/resolved + empty;
 *  `attention` narrows to the stale ones needing action; `past` shows resolved/merged. */
export type DisruptionView = 'active' | 'attention' | 'past';

export interface DisruptionFilters {
  status?: DisruptionStatus | undefined;
  cause?: DisruptionCause | undefined;
  view?: DisruptionView | undefined;
  /** Header airport scope (NEX-310) — CSV of IATA codes (NEX-308 wire). */
  airports?: string | undefined;
}

export interface CreateDisruptionInput {
  cause: DisruptionCause;
  title: string;
  description?: string;
  scope: DisruptionScope;
}

/** Cursor page envelope (FROZEN — `{ items, nextCursor }`, no total). */
export interface IPage<T> {
  items: T[];
  nextCursor: string | null;
}

export const DISRUPTION_STATUSES: readonly DisruptionStatus[] = [
  'DETECTED',
  'CONTAINED',
  'RESOLVED',
  'MERGED',
];

export const DISRUPTION_CAUSES: readonly DisruptionCause[] = [
  'WEATHER',
  'MECHANICAL',
  'STRIKE',
  'ATC',
  'SECURITY',
  'IT_OUTAGE',
  'OTHER',
];

/** Airport dropdown option (BFF /v1/disruptions/airports). */
export interface AirportOption {
  code: string;
  label: string;
}

/** One affected-flight row for the wizard picker. Mirrors operator-bff IPredictorFlightRow. */
export interface UpcomingFlight {
  flightUrn: string;
  flightNumber: string;
  carrierCode: string;
  origin: string;
  destination: string;
  scheduledDepartureUtc?: string;
  /** Scheduled arrival instant — tells an in-flight arrival from one that already landed. */
  scheduledArrivalUtc?: string;
  aircraftType?: string;
  paxCount: number;
  status?: string;
  cancelProbability?: number;
  expectedDelayMinutes?: number;
}

export type AirportDirection = 'any' | 'origin' | 'destination';

/** Result of batch case-create (POST /v1/disruptions/:urn/cases). */
export interface CreateDisruptionCasesResult {
  created: { flightUrn: string; caseUrn: string }[];
  /**
   * Flights skipped because they're ALREADY in a disruption (NEX-288 dedup — a
   * flight's case belongs to at most one disruption, so no second case is minted).
   * `existingDisruptionUrn` is the parent disruption to link to, when it has one.
   */
  skipped: { flightUrn: string; existingCaseUrn: string; existingDisruptionUrn?: string }[];
  failed: { flightUrn: string; error: string }[];
  /**
   * NEX-300: true when the fan-out created ZERO cases (all flights skipped/failed)
   * and the BFF discarded the now-empty disruption it had just created. The UI must
   * NOT navigate to the (deleted) disruption — it shows a "no disruption created"
   * done-state instead. Absent/false on a normal create (≥1 case landed).
   */
  discarded?: boolean;
}

/* ── S7 (NEX-282) — control-center roster · progress · cost (FROZEN S5 shapes) ── */

/** Per-passenger rebooking lifecycle the operator filters by (mirrors the BFF). */
export type RosterRebookingStatus =
  | 'NOT_APPLICABLE'
  | 'PENDING'
  | 'OPTIONS_READY'
  | 'SELECTED'
  | 'NO_OPTIONS'
  | 'OVERRIDDEN'
  | 'DEFERRED';

/**
 * One passenger row on the disruption roster (GET /v1/disruptions/:urn/passengers).
 * FROZEN S5 shape: friendly labels primary (`fullName`, `flightLabel`), URNs secondary.
 */
export interface DisruptionPaxRosterRow {
  fullName: string;
  firstName: string;
  lastName: string;
  paxUrn: string;
  subCaseUrn: string;
  caseUrn: string;
  pnrUrn: string;
  paxType: string;
  loyaltyTier?: string;
  isSenior?: boolean;
  /** Accommodation condition (#806, `cases:roster.condition@v1`, additive). Absent =
   *  never evaluated; `UNKNOWN` holds the whole party back from hotel / transport
   *  assignment until the desk sets it or links a responsible adult. */
  condition?: PassengerCondition;
  /** Why it holds that value: EXPLICIT · SSR · PAX_TYPE · DATE_OF_BIRTH · RESPONSIBLE_ADULT · UNRECOGNIZED · NONE · OPERATOR. */
  conditionBasis?: string;
  conditionSource?: 'MANIFEST' | 'OPERATOR';
  conditionSetBy?: string;
  /** Display name of that operator (#813, additive) — the roster tooltip says "marcado por X". */
  conditionSetByName?: string;
  conditionSetAt?: string;
  responsibleAdult?: ResponsibleAdultRef;
  status: string;
  specialNeeds?: string[];
  priorityTags: string[];
  flightLabel: string;
  flightUrn: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureAt: string;
  onwardFlightLabel?: string;
  onwardFlightUrn?: string;
  onwardNumber?: string;
  onwardOrigin?: string;
  onwardDestination?: string;
  onwardDepartureAt?: string;
  rebookingStatus: RosterRebookingStatus;
  optionsCount: number;
  selectedOptionUrn?: string;
  /** Hotel check-in confirmation (transport-desk T7, additive — the cases K3
   *  write denormed onto the roster by K5): the instant the passenger (or an
   *  operator) confirmed arrival at the hotel. Absent = not checked in yet
   *  (or an older BFF that doesn't carry the denorm). ISO-8601. */
  checkedInAt?: string;
  /** Who confirmed it: `PASSENGER_REPLY` (the SMS/WhatsApp loop) | `OPERATOR`
   *  (marked from the desk). Tolerates future sources as plain strings. */
  checkInSource?: 'PASSENGER_REPLY' | 'OPERATOR' | string;
  /** Operator behind an OPERATOR check-in (#813, additive): URN + display name.
   *  Absent on a passenger reply and on stamps written before #813. */
  checkedInBy?: string;
  checkedInByName?: string;
  /** passenger-profile-completion (`cases:roster.profile@v1`, additive): who last
   *  completed THIS passenger's entry (from the counter QR or the desk) and the
   *  travel-document KIND they declared — never a number. Absent = manifest as ingested. */
  profileSource?: 'PASSENGER_SELF_SERVICE' | 'OPERATOR';
  documentType?: 'PASSPORT' | 'NATIONAL_ID' | 'OTHER';
  /** The GROUP's completion stamp + contact presence, stamped by the BFF onto every
   *  row of the party so a passengers card can read it off `passengers[0]`. */
  profile?: PnrGroupProfile;
  contactOnFile?: { phone: boolean; email: boolean };
}

/** Profile completion (`cases:roster.profile@v1`). */
export interface PnrGroupProfile {
  completedAt: string;
  source: 'PASSENGER_SELF_SERVICE' | 'OPERATOR';
  version: number;
}

/** Cursor-paginated roster page (FROZEN envelope — `{ items, nextCursor }`). */
export interface DisruptionPaxRosterPage {
  items: DisruptionPaxRosterRow[];
  nextCursor: string | null;
}

/* ── S12 (NEX-287) — PNR-grouped roster ──────────────────────────────────────── */

/** Pax-type breakdown of a PNR group (adults / children / infants). */
export interface DisruptionPaxBreakdown {
  adt: number;
  chd: number;
  inf: number;
}

/**
 * One PNR group on the disruption roster (GET /v1/disruptions/:urn/passengers).
 * FROZEN S5 group shape: the PNR (one sub-case = one PNR = the booking/family unit)
 * is the operator's actionable row; the member passengers ride nested under it.
 * Friendly labels primary (`pnrLabel`, `flightLabel`, `leadName`), URNs secondary.
 */
/** The bundled offer on a roster row (mirror of the cases sub-cases `offer`). */
export interface RosterOffer {
  kind: 'FLIGHT_AND_HOTEL' | 'REBOOKING' | 'HOTEL' | 'MEAL_VOUCHER' | 'TRANSFER' | 'OTHER';
  hash: string;
  ready: boolean;
  summary?: string;
  parts?: {
    flight?: {
      selectedOptionUrn: string;
      flightNumber?: string;
      origin?: string;
      destination?: string;
      departureAt?: string;
      seat?: string;
      cabin?: string;
      holdExpiresAt?: string;
    };
    hotel?: {
      hotelUrn: string;
      reservationUrn?: string;
      reservationStatus: 'HELD' | 'CONFIRMED';
      hotelName?: string;
      checkInUtc?: string;
      checkOutUtc?: string;
      /** Persisted reservation counts (cases `IOfferHotelPart`); absent when unknown. */
      rooms?: number;
      nights?: number;
      holdExpiresAt?: string;
    };
    voucher?: { amountUsd?: number; currency?: string; issued: boolean };
  };
}

/**
 * Where a stay-home passenger is going, as the roster is allowed to say it: a
 * point (GeoJSON `[longitude, latitude]`), a coarse locality label, who resolved
 * it, or why nothing did. Never an address.
 */
export interface HomeDestination {
  coordinates?: [number, number];
  /** Coarse locality ("Santiago"), never the street. */
  label?: string;
  geocodedBy?: 'nominatim' | 'mock' | 'manual' | string;
  /** `NOT_FOUND` — the geocoder answered and found nothing; `UNAVAILABLE` — it could not answer. */
  geocodeError?: 'NOT_FOUND' | 'UNAVAILABLE' | string;
}

/** Current hotel-arrival inquiries, filtered by Cases against booking and trip identity. */
export interface RosterArrivalInquiry {
  inquiryUrn: string;
  passengerUrn: string;
  tripUrn: string;
  hotelUrn: string;
  status: 'PENDING' | 'ARRIVED_OK' | 'PROBLEM';
  requestedAt: string;
  respondedAt?: string;
}

export interface DisruptionPnrGroupParty {
  role: 'ROOT' | 'MEMBER';
  rootSubCaseUrn: string;
  rootPnrUrn: string;
  rootPnrLabel: string;
  /** Every sub-case of the party, root first. */
  subCaseUrns: string[];
  pnrUrns: string[];
  pnrLabels: string[];
}

/**
 * Where a PNR in a case came from (`cases:roster.pnrSource@v1`): an imported file, the
 * airline, or generated data (`SYNTHETIC`) — the passenger-data slice of the provenance
 * vocabulary, so the labels and tones are `SourceBadge`'s.
 */
export type PnrSource = Extract<FlightDataSource, 'FILE' | 'SERVICE_PROVIDER' | 'SYNTHETIC'>;

export interface DisruptionPnrGroupRow {
  arrivalInquiries?: RosterArrivalInquiry[];
  pnrLabel: string;
  pnrUrn: string;
  /**
   * Where this PNR came from, as cases stored it when it read the manifest (additive).
   * `SYNTHETIC` = generated data (a mocked airline adapter or the seeded rung, demo only):
   * the screens that list or detail this row say «Sintético» (`PnrSourceMark` names them). Absent =
   * unknown (a case opened before cases stored it, a PNR typed at the counter) — never
   * synthetic.
   */
  pnrSource?: PnrSource;
  subCaseUrn: string;
  caseUrn: string;
  /** Parent disruption (denormalised) — routes the case-scoped operation
   *  center's disruption-scoped care actions (allocate / voucher / exclude)
   *  through the PNR's parent disruption. Absent for a standalone case. */
  disruptionUrn?: string;
  flightLabel: string;
  flightUrn: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureAt: string;
  rebookingStatus: RosterRebookingStatus;
  optionsCount: number;
  /** The group's assistance needs (SSR/special-need codes — WCHR, PETC, UMNR, …)
   *  governing SSR-aware reassignment (rebooking-engine S2). */
  needs?: string[];
  /** Connection PNR (rebooking-engine S4): the final destination + stops, so the
   *  operator protects the FINAL destination, not just the disrupted leg. */
  connection?: { finalDestination: string; via?: string[] };
  /** The full booked itinerary — every leg + final destination, surfaced ALWAYS so
   *  the PNR drawer shows the legs + final destination with or without a rebooking. */
  itinerary?: {
    isConnection: boolean;
    finalDestination?: string;
    legs: Array<{
      flightNumber?: string;
      origin?: string;
      destination?: string;
      /** Scheduled instants (ISO-8601 UTC) when the manifest carried them — lets the
       *  drawer show the ONWARD connection's time. Absent in mock-manifest mode. */
      scheduledDeparture?: string;
      scheduledArrival?: string;
    }>;
  };
  /** Issued mock-PSS ticket number (rebooking-engine S5); present once ticketed. */
  ticketNumber?: string;
  selectedOptionUrn?: string;
  /** Server-truth seat-hold clock (rebooking holdExpiresAt@v2), attached by the BFF
   *  from the same options view the drawer reads. Absent = no clock, never expired. */
  holdExpiresAt?: string;
  holdSource?: 'INTERNAL' | 'VENDOR';
  /** The flight providers rebooking's latest search asked for this PNR and why each did or
   *  did not answer — attached by the BFF on a NO_OPTIONS row only (same options view the
   *  drawer reads). Absent = unknown. */
  flightProviderAttempts?: FlightProviderAttempt[];
  /** The provider of the SELECTED option (`duffel` / `atlas`: Nexa buys the flight). Absent = unknown. */
  selectedProvider?: string;
  /** Every option rebooking holds for this PNR already left (the options view's soft mark,
   *  attached by the BFF): the offers expired — a new search is the way out. Absent while any
   *  option is still live, on a ticketed row, or when rebooking could not be read. */
  optionsExpired?: boolean;
  /** The automatic purchase of that flight when Nexa buys it (owner decision 5, 2026-10-30):
   *  comprando / emitido / «Te necesitan» with the reason. Absent = none started. */
  nexaPurchase?: NexaPurchase;
  /** The assigned (re-accommodated) flight — present once SELECTED ("PNR → new flight"). */
  assignedFlight?: {
    number?: string;
    origin?: string;
    destination?: string;
    departureAt?: string;
    /** Assigned seat (e.g. "14C"), the operator-facing "why", and the cabin
     *  (rebooking-engine S1). Present once a concrete seat is assigned. */
    seat?: string;
    seatReason?: string;
    cabin?: string;
  };
  /** The next flight the AIRLINE named in its manifest import (cases
   *  `reaccommodation`, source MANIFEST — `cases:roster.reaccommodation@v1`,
   *  additive via the BFF). Never a Nexa rebooking choice: on a care-only case it
   *  is the only flight the drawer may show. Absent when the manifest named none. */
  manifestNextFlight?: {
    flightNumber: string;
    /** ISO-8601 UTC departure instant. */
    departureUtc: string;
    origin?: string;
    destination?: string;
  };
  /** Whether the assigned flight needs an overnight hotel (derived from its
   *  schedule). Present once SELECTED; false = same-day/next-morning (no hotel). */
  requiresHotel?: boolean;
  /** WHY `requiresHotel` holds its value (rebooking §12 vocabulary). `OVERRIDDEN`
   *  with `requiresHotel === false` = an explicit suppression (e.g. the stay-home
   *  declaration) that wins at any rebooking status. Pass-through from cases. */
  hotelRequirementReason?: string;
  /** The passenger's (or the operator's declared) election on the sub-case
   *  rebooking state. `LOCAL_RESIDENT` = operator declared "stays at own home —
   *  disrupted at home base": rebooking keeps running, no hotel demand. The BFF
   *  passes the sub-case field through on the roster row; absent on older rows. */
  passengerElection?: string;
  /** The accommodation outcome decided by rebooking (resolution-engine §4):
   *  HOTEL (overnight) / MEAL_VOUCHER (long non-overnight wait) / NONE (short gap).
   *  Drives the roster chip; falls back to `requiresHotel` when absent. */
  accommodationKind?: 'HOTEL' | 'MEAL_VOUCHER' | 'NONE';
  /** IATA where the pax actually SPENDS the night (hotel geo anchor) — the stranding
   *  station, or the CONNECTION HUB when the night falls in a hub layover (e.g.
   *  PMC→SCL tonight, SCL→ANF next morning ⇒ `SCL`). Absent on legacy groups. */
  overnightStation?: string;
  /** Ride-home destination of a `LOCAL_RESIDENT` group (`cases:roster.homeDestination@v1`,
   *  additive, via the BFF): present only when the stay-home declaration carried an
   *  address. `coordinates` gates the "Ride home" action; a `geocodeError` row never
   *  offers one. The street is NOT here — it stays behind an audited reveal. */
  homeDestination?: HomeDestination;
  /** Meal-voucher entitlement when `accommodationKind === 'MEAL_VOUCHER'`. */
  mealVoucher?: {
    amountUsd: number;
    currency: string;
    includesLounge?: boolean;
    /** Set once wallet confirms the voucher card — the chip flips to "Voucher issued". */
    issued?: boolean;
    cardUrn?: string;
    /** Settled top-up total in MINOR units (cases K3, `wallet.card-adjusted` denorm).
     *  Additive; absent until a top-up settles. */
    toppedUpMinor?: number;
    /** Benefit consumed (#808, cases denorm of `wallet.card-redeemed`). */
    redeemedAt?: string;
    redemptionSource?: 'OPERATOR_SIMULATED' | 'ISSUER';
    redeemedBy?: string;
    redemptionReference?: string;
    redeemedAmount?: number;
  };
  /** Governing-policy provenance — the policy URN + resolver match scope that governs
   *  this PNR (POL-12 case governing, resolved at ingestion; falls back to the POL-11
   *  rebooking-option policy), so the drawer can disclose which policy governed it. */
  governingPolicyUrn?: string;
  policyMatchType?: string;
  /** Plain-language reason the governing policy won (resolve `explain.reason`, POL-12). */
  policyMatchReason?: string;
  /** Coded reason this group's `rebookingStatus === NO_OPTIONS` / `NOT_APPLICABLE`
   *  (POLICY_DISABLED, NO_INVENTORY, FILTERED_OUT, NO_ROUTE, REJECTED_ALL, HOLD_EXPIRED).
   *  Mapped to human copy via `NO_OPTIONS_REASON_LABEL`; absent → generic fallback. */
  noOptionsReason?: string;
  /** Declared accommodation outcome (hotel desk): the PNR was resolved WITHOUT a
   *  hotel — the passenger manages their own stay (voucher issued), the airline
   *  formally has nothing to offer, or (cases K3) the group waits at the AIRPORT
   *  for its onward flight — resolved-without AND care-entitling. Additive;
   *  absent on an older BFF/cases. */
  accommodationOutcome?: {
    outcome: 'SELF_MANAGED' | 'NO_ACCOMMODATION' | 'AIRPORT_WAIT';
    note?: string;
    decidedByUrn?: string;
    /** Display name of the declaring operator (#813, additive). */
    decidedByName?: string;
    decidedAt?: string;
    /** #811 additive: `PASSENGER` = "prefiero ir a mi domicilio" chosen in the pax app. */
    source?: 'OPERATOR' | 'PASSENGER';
    /** PASSENGER SELF_MANAGED: the home address (PII — roster chip + drawer only). */
    address?: string;
    wantsTransportHome?: boolean;
    /** Advisory return pickup to the airport when a ride home was requested. */
    returnPickupAt?: string;
  };
  /** Declared care outcome (care desk, cases K3): the group's meal/care
   *  entitlement was settled OUTSIDE the wallet rails — it ends the group's card
   *  story. Additive; absent unless declared. */
  careOutcome?: CareOutcome;
  /** AIRPORT_WAIT meal-grant suggestion (cases K3) — present ONLY when
   *  `accommodationOutcome.outcome === 'AIRPORT_WAIT'`: the wait-window
   *  derivation, or an EXPLICIT `null` while no departure is assigned yet.
   *  A suggestion only — money moves via the wallet top-up proxy. */
  careSuggestedGrant?: CareSuggestedGrant | null;
  /** The denormalised hotel reservation for this PNR (`compensationData.reservationUrn`),
   *  present once a hotel is held/booked. The drawer matches the case's live
   *  reservations by it to surface the reservation status. */
  hotelReservationUrn?: string;
  /** `HELD` (reversible offer awaiting passenger acceptance) | `CONFIRMED` (booked). Absent = booked (legacy). */
  hotelReservationStatus?: 'HELD' | 'CONFIRMED';
  lastOfferResponse?: {
    response: 'ACCEPTED' | 'DECLINED';
    at: string;
    offerHash?: string;
    hotelReservationUrn?: string;
    declineScope?: 'HOTEL' | 'BUNDLE';
    /** `CALLBACK_REQUESTED` — «prefiero que me llamen» (accepted-offer-recovery §6.7). */
    declineIntent?: 'OTHER_OFFERS' | 'STAY_HOME' | 'FREE_TEXT' | 'CALLBACK_REQUESTED';
    reason?: string;
  };
  /** The reserved hotel (`compensationData.hotelUrn`). */
  hotelUrn?: string;
  /** Acceptance bundle (additive, cases-owned): the offer as ONE thing — its kind,
   *  whether every reversible part exists (`ready`), and its parts; plus what has
   *  been committed after acceptance. Absent on an older BFF/cases. */
  offer?: RosterOffer;
  commit?: { ticket: boolean; hotel: boolean; complete: boolean };
  /** The cases sub-case lifecycle status (PENDING · PROCESSING · AWAITING_ACCEPTANCE ·
   *  OFFER_READY · RESOLVED · DECLINED …). Additive; absent on an older BFF. */
  subCaseStatus?: string;
  /** MASS ground transport (additive, cases-owned `roster.transfer@v1`): the group's
   *  airport→hotel transfer as the roster sees it — trip status, kind (CAR per pax
   *  vs SHUTTLE run), the run number + pickup time + boarding badge for shuttles. */
  transfer?: RosterTransfer;
  /** The group's live Uber voucher (additive, cases-owned `roster.transportVoucher@v1`).
   *  A voucher IS ground transport — the passenger rides whenever they want. */
  transportVoucher?: RosterTransportVoucher;
  /** Declared ground-transport outcome (transport-desk, additive — the T4 write):
   *  the operator recorded that this PNR travels by its own means (or needs no
   *  ride). Ends the PNR's transport story, mirroring `accommodationOutcome`. */
  transportOutcome?: { outcome: 'OWN_MEANS' | string; note?: string; decidedByUrn?: string; decidedByName?: string; decidedAt?: string };
  paxCount: number;
  paxBreakdown: DisruptionPaxBreakdown;
  /** Priority chips — UNION across the group members. */
  priorityTags: string[];
  /** Minors guard (#806, additive): UNKNOWN members + the party's blockers
   *  (`CONDITION_UNKNOWN`). Absent when the party may be assigned. */
  conditionUnknownPaxUrns?: string[];
  allocationBlockers?: string[];
  /** Allocation party (#806 slice 2, additive): this PNR is assigned WITH the linked
   *  PNRs (responsible adult on another booking) — same hotel, same vehicle. */
  party?: DisruptionPnrGroupParty;
  leadName: string;
  passengers: DisruptionPaxRosterRow[];
  /** passenger-profile-completion (`cases:roster.profile@v1`, additive): the
   *  party's completion stamp and whether a phone / email is on file (booleans —
   *  the values stay behind the audited reveal). Absent until a profile write lands. */
  profile?: PnrGroupProfile;
  contactOnFile?: { phone: boolean; email: boolean };
}

/** The roster row's transfer slice (`cases:roster.transfer@v1`, additive). `status`
 *  is the trip status (REQUESTED · DISPATCHED · IN_TRANSIT · COMPLETED · CANCELLED ·
 *  FAILED …); `boardingStatus` the seat's boarding for a SHUTTLE run. */
export interface RosterTransfer {
  /** `AIRPORT_TO_HOME` = the ride home of a stay-home passenger (CAR only). */
  leg: 'AIRPORT_TO_HOTEL' | 'HOTEL_TO_AIRPORT' | 'AIRPORT_TO_HOME';
  status: string;
  kind?: 'CAR' | 'SHUTTLE';
  runSeq?: number;
  /** ISO-8601. */
  pickupTime?: string;
  boardingStatus?: 'ASSIGNED' | 'BOARDED' | 'NO_SHOW';
}

/** The roster row's voucher slice (`cases:roster.transportVoucher@v1`, additive). Never
 *  CANCELLED (cases drops it). `paxUrns` = the passengers holding a code not cancelled;
 *  empty while the program is still being issued (it speaks for the whole group then). */
export interface RosterTransportVoucher {
  programUrn: string;
  scenario: 'HOTEL_OVERNIGHT' | 'RIDE_HOME' | 'ONE_WAY';
  status: 'ISSUING' | 'CODES_PENDING' | 'ACTIVE';
  /** Epoch ms — the care window's end. */
  endsAt: number;
  paxUrns: string[];
}

/** Cursor-paginated PNR-grouped roster page (FROZEN envelope — `{ items, nextCursor }`). */
export interface DisruptionPnrGroupPage {
  items: DisruptionPnrGroupRow[];
  nextCursor: string | null;
}

/** Roster server-side filters (the chips + search pushed to the query). */
/** Secondary ordering WITHIN each flight group (the roster is always grouped by
 *  flight first). Applied SERVER-side so the order is stable across the whole
 *  roster, not just the loaded pages. */
export type RosterSort = 'default' | 'pnr' | 'flight' | 'status' | 'pax' | 'needs';

export interface DisruptionRosterFilters {
  priority?: string | undefined;
  rebookingStatus?: RosterRebookingStatus | undefined;
  q?: string | undefined;
  /** Narrow to one disrupted flight (exact flightUrn match, server-side). */
  flightUrn?: string | undefined;
  /** Within-flight ordering (server-side compound keyset). */
  sort?: RosterSort | undefined;
  /** Page size (BFF default 50, hard max 200). Absent = the server default. */
  limit?: number | undefined;
}

/* ── disruption-roster-mvp-parity (epic NEX-369 / U1) — roster header tiles ────
 * UI mirror of the FROZEN operator-bff contract (slice B1,
 * apps/operator-bff/src/modules/disruptions/disruptions.types.ts · IRosterSummary).
 * Kept byte-compatible; do NOT import from operator-bff. Read-aggregation only —
 * every field is a count / minor-unit total, never PII. */

/**
 * The 4 MVP header tiles for the disruption Passengers roster
 * (operator-bff:GET /v1/disruptions/{urn}/roster-summary@v1). Composed server-side
 * from EXISTING reads (the disruption rollup + the rebooking-detail group results);
 * degrades open — any upstream miss yields zeros, never throws.
 */
export interface RosterSummary {
  /** Affected flights (a case ≈ a flight) — the rollup's `caseCount`. */
  affectedFlights: number;
  /** Affected passengers — the rollup's `affectedPassengers`. */
  passengers: number;
  /**
   * Connections at risk: `total` = groups with a best option; `count` = those whose
   * best option is onward-infeasible OR forces an overnight (`count <= total`).
   */
  connectionsAtRisk: { count: number; total: number };
  /** Headline estimated PRICE in minor units (cents) — the rollup's `totalPriceMinor`;
   *  null = not available («No disponible»). */
  estimatedPriceMinor: number | null;
  /** Tenant currency of the estimate (rollup `currency`, default 'USD'). */
  currency: string;
  /** false ⇒ `estimatedPriceMinor` is null. */
  priced?: boolean;
}

/** An in-flight bulk run for a disruption (S16 / NEX-292). Carried on the progress
 * poll once the domain projects it, so the control center disables the bulk bar
 * while another operator's run is live. Optional — a cold/older BFF omits it. */
export interface DisruptionActiveRun {
  /** Which op is running: durable domain runs (rebooking/allocation) or the BFF's
   *  synchronous per-case fan-outs (tickets/acceptance/vouchers/transfers). */
  op: 'rebooking' | 'allocation' | 'tickets' | 'acceptance' | 'vouchers' | 'transfers';
  /** Correlates with the runId returned by the trigger. */
  runId: string;
  /** Who started it (URN). The bar shows "Running — started by {operator}". */
  operatorUrn: string;
  startedAt: string;
  /** Live counters of a synchronous fan-out (advance per case over the WS). Transfers
   *  tick per hotel (`hotels` — the BFF has always emitted it). */
  progress?: { done: number; total: number; unit: 'cases' | 'groups' | 'hotels' };
}

/** Group-level resolution facts (Σ over the disruption's cases; cases + booking). */
export interface DisruptionGroupFacts {
  groups: number;
  notApplicable: number;
  rebookable: number;
  assigned: number;
  optionsReady: number;
  noOptions: number;
  /** Of `noOptions`: every option they had already LEFT (rebooking `OPTIONS_DEPARTED`) — a
   *  search away, never «review by hand» (PO 2026-11-03). Absent on an older BFF. */
  optionsDeparted?: number;
  open: number;
  assignedPax: number;
  ticketed: number;
  hotelNeeded: number;
  hotelUndecided: number;
  hotelHeld: number;
  hotelConfirmed: number;
  voucherEligible: number;
  voucherIssued: number;
  voucherNoKyc: number;
  awaiting: number;
  accepted: number;
  declined: number;
  resolved: number;
  atGate: number;
  /** Groups the operator excluded from the case (not in `groups`). */
  excluded: number;
  /** Boarding proxy: ticket issued AND assigned flight already departed. */
  travelled: number;
  /** Ticket issued, flight not departed yet. */
  ticketedNotDeparted: number;
  /* ── acceptance bundle (additive; absent on an older BFF) ─────────────────
   * The passenger accepts ONE offer = flight (seat on HOLD) + hotel (HOLD) when
   * the group overnights, or flight only. `offerReady` = every reversible part
   * exists; `offered` = the offer went out (== atGate); `flightOnly` = offer-ready
   * groups that need no hotel; `issued` = ticket issued AND hotel confirmed
   * (automatic after acceptance); `acceptedNotIssued` = commit still in flight;
   * `commitFailed` = commit stale beyond the BFF threshold. */
  offerReady?: number;
  offered?: number;
  flightOnly?: number;
  issued?: number;
  acceptedNotIssued?: number;
  commitFailed?: number;
  /** The flights Nexa BUYS (Duffel / Atlas) and their automatic purchase (decision 5,
   *  2026-10-30): those groups are NOT in `acceptedNotIssued` / `commitFailed`. Absent on an
   *  older BFF, or when rebooking's purchase read failed. */
  purchases?: PurchaseFacts;
  known: { cases: boolean; booking: boolean };
}

/** `groups.purchases` — the accepted groups whose flight Nexa buys, by where their purchase is. */
export interface PurchaseFacts {
  /** A purchase queued or running. */
  buying: number;
  /** Bought: ticketed from a confirmed order. */
  bought: number;
  /** In «Te necesitan», by reason. */
  needsReview: number;
  /** Accepted, unticketed, no purchase running — «Emitir N aceptadas» starts it. */
  idle: number;
  byReason: Record<string, number>;
  /* `operator-bff:purchase-recovery@v1` (§6.6): the failed purchases by where their recovery is.
   * An active recovery counts HERE only — never in buying / needsReview / idle. Always sent by
   * the BFF that has §6.6; absent from an older one. */
  /** Nexa searches again or buys an equivalent (`SEARCHING`, `AUTO_BUYING`). */
  replacing?: number;
  /** A new option is with the passenger (`REOFFERED`). */
  reoffered?: number;
  /** An unconfirmed purchase is being verified with the supplier (`CONFIRMING`). */
  confirming?: number;
  /** «Te necesitan» for the recovery (`NEEDS_OPERATOR`). */
  needsOperator?: number;
  /** Recovered — counted wherever the group now is (informational). */
  recovered?: number;
  /** `needsOperator` by reason (`NO_OPTIONS`, `REOFFER_TIMEOUT`, …; `UNKNOWN` without one). */
  needsOperatorByReason?: Record<string, number>;
  /** NEEDS_OPERATOR groups whose latest search left at least one candidate. */
  needsOperatorWithOptions?: number;
  /** NEEDS_OPERATOR groups whose latest search left none (or an unknown count) — the ones worded
   *  «sin opciones». Never AUTO_BUY_LIMIT (its options may still exist), so `needsOperator −
   *  WithOptions − NoOptions` = the AUTO_BUY_LIMIT ones without a count. */
  needsOperatorNoOptions?: number;
  /** NEEDS_OPERATOR groups whose last answer was a «prefiero que me llamen» decline
   *  (`CALLBACK_REQUESTED`) — disjoint: never also in WithOptions / NoOptions. */
  needsOperatorCallback?: number;
}

/**
 * The automatic purchase of one group's flight (rebooking `nexa-purchase/`, via operator-bff;
 * the BFF's `INexaPurchaseView`). A group may carry a recovery WITHOUT a purchase about its
 * selected flight (a re-offer moved the selection, Nexa is searching again): the BFF still
 * sends `nexaPurchase`, with `recovery`, a stand-in `status` for older readers
 * (SEARCHING/AUTO_BUYING/CONFIRMING → BUYING, REOFFERED → RERESOLVED, NEEDS_OPERATOR →
 * NEEDS_REVIEW, RECOVERED → BOUGHT), `attempts: 0` and empty `queuedAt` / `updatedAt`.
 * Read `recovery` before `status`.
 */
export interface NexaPurchase {
  status: 'QUEUED' | 'BUYING' | 'BOUGHT' | 'NEEDS_REVIEW' | 'RERESOLVED';
  /** `OVER_PRICE_CAP`, `MISSING_DATA`, `PASSPORT_REQUIRED`, `PROVIDER_ERROR`, … (`NEEDS_REVIEW`). */
  reason?: string;
  /** A vendor / domain code behind the reason. */
  detail?: string;
  /** Exactly which data is missing, per passenger URN. */
  missing?: Array<{ paxUrn?: string; field: string }>;
  provider: string;
  purchaseId?: string;
  ticketNumber?: string;
  previousOptionUrn?: string;
  /** PRICE vs the cap, USD cents — per passenger, or the option's total for a bulk run's cap. */
  priceMinor?: number;
  capMinor?: number;
  /** Which cap: the policy's per passenger, or the bulk run's. */
  capBasis?: 'PER_PASSENGER' | 'RUN';
  attempts: number;
  queuedAt: string;
  updatedAt: string;
  /** `operator-bff:purchase-recovery@v1` (additive): where the recovery of a failed purchase
   *  is. Absent = no recovery (an older BFF, or a purchase that never failed). */
  recovery?: NexaPurchaseRecovery;
}

/** The roster row's recovery summary (`nexaPurchase.recovery`, §6.6). The BFF never sends
 *  `NONE` (absent instead); readers still treat a `NONE` as no recovery. */
export interface NexaPurchaseRecovery {
  state: RecoveryState;
  /** A machine code: the failure (`OFFER_GONE`, …) or why an operator is needed (`NO_OPTIONS`,
   *  `REOFFER_TIMEOUT`, `POLICY_OPERATOR`, `UNCONFIRMED_EXPIRED`, `INVALID_TRAVELER_DATA`). */
  reason?: string;
  /** While REOFFERED: the passenger's deadline to accept (ISO-8601). */
  acceptBy?: string;
  /** How many options the latest fresh search left, and how many are equivalent (§6.6
   *  amendment 2026-10-31). Absent = unknown — never «no flights». */
  candidates?: number;
  equivalent?: number;
}

/* ── `operator-bff:purchase-recovery@v1` (accepted-offer-recovery §6.6) — the recovery of a
 * group whose accepted flight could no longer be bought: rebooking's view (§6.2), re-shaped by
 * the BFF and priced for the airline (E8: the airline's price, never cost). Codes, flight
 * facts and times only — never traveler data. */

/** Where the recovery of one group stands. */
export type RecoveryState =
  | 'NONE'
  | 'SEARCHING'
  | 'AUTO_BUYING'
  | 'REOFFERED'
  | 'NEEDS_OPERATOR'
  | 'CONFIRMING'
  | 'RECOVERED';

/** Why the purchase failed (rebooking E2: the supplier's error, classified). */
export type PurchaseFailureReason = 'OFFER_GONE' | 'PRICE_CHANGED' | 'INVALID_TRAVELER_DATA' | 'UNCONFIRMED' | 'PROVIDER_ERROR';

/** Why a candidate is not equivalent to the accepted flight (P3). `NO_CAPACITY` — no room for
 *  the group (§6.1 amendment 2): never equivalent, never offered. */
export type CandidateWhy =
  | 'ARRIVES_LATER'
  | 'OTHER_DAY'
  | 'EXTRA_HOTEL_NIGHT'
  | 'LOWER_CABIN'
  | 'OVER_CAP'
  | 'TOO_SOON'
  | 'NO_CAPACITY';

export interface IRecoveryFlight {
  optionUrn: string;
  flightNumber: string;
  /** ISO-8601 UTC. */
  departAt: string;
  arriveAt: string;
  cabin: string;
}

export interface IRecoveryCandidate extends IRecoveryFlight {
  provider: string;
  equivalent: boolean;
  why: CandidateWhy[];
  arrivalDelayMinutes: number;
  /** The whole party's fare at the airline's PRICE, minor units of `currency`; `null` when no
   *  fare is known or the price card is unavailable (D17) — never the cost. */
  priceMinor: number | null;
  currency: string | null;
  /** The accepted flight itself, freshly offered by the supplier: bought under the original
   *  acceptance, never re-offered (RB1, additive). */
  sameAsAccepted?: boolean;
}

export interface IRecoveryCheck {
  at: string;
  result: 'NOT_FOUND' | 'FOUND' | 'ERROR';
}

/** `GET /v1/groups/:groupId/recovery?caseUrn=` (and every per-group write's answer). */
export interface IRecoveryView {
  groupId: string;
  caseUrn: string;
  state: RecoveryState;
  failedAttempt?: {
    attemptId: string;
    optionUrn: string;
    reason: PurchaseFailureReason | string;
    /** The supplier's own code (`offer_no_longer_available`, `DUFFEL_HTTP_422`). */
    vendorCode?: string;
    /** The traveler FIELD the supplier refused — never its value. */
    field?: string;
    at: string;
  };
  /** The flight the passenger accepted. Absent only when rebooking names none (`NONE`). */
  accepted?: IRecoveryFlight;
  /** The fresh search's results (never the stale options of the first search). */
  candidates: IRecoveryCandidate[];
  searchedAt?: string;
  reoffer?: { optionUrn: string; sentAt: string; acceptBy: string };
  unconfirmed?: {
    attemptRef: string;
    checks: IRecoveryCheck[];
    nextCheckAt?: string;
    giveUpAt?: string;
  };
  /** `false` → the price card was unavailable and every Nexa-bought `priceMinor` is `null`. */
  priced: boolean;
}

/** `POST /v1/disruptions/:urn/recovery/{search,offer-best}` → 202 (lock + fan-out + sync-run). */
export interface IRecoveryBulkResult {
  disruptionUrn: string;
  /** Per-outcome counts of the fan-out. */
  tally: Record<string, number>;
}

/** Run lifecycle straight off rebooking's durable run marker (NEX-313).
 *  `PARTIAL` = the run generated but some groups were degraded by a rate-limited/
 *  erroring vendor — the operator re-runs just those. */
export type RunStatus = 'NONE' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';

/**
 * Live rebooking-progress + run-state (GET /v1/disruptions/:urn/progress, then
 * pushed over the WS). NEX-313: `runStatus` + `processedCases`/`totalCases` come
 * from rebooking's DURABLE run marker (restart-safe, authoritative) — they drive
 * the live progress bar during a run and make `activeRun` truthful (no ghost
 * "Running" after a dead run). The resolved/pending counts stay the S4 rollup.
 */
export interface DisruptionProgress {
  urn: string;
  status: DisruptionStatus;
  groupsTotal: number;
  optionsGenerated: number;
  resolved: number;
  pending: number;
  deferred: number;
  resolvedFraction: number;
  /** NEX-313 — the run's own authoritative lifecycle + forward progress. */
  runStatus: RunStatus;
  processedCases: number;
  totalCases: number;
  /** Live per-group progress — groups settled so far + the planned total. Drives
   *  the dialog's smooth group-by-group bar (no frozen 0%→100% jump on one big case). */
  processedGroups?: number;
  plannedGroups?: number;
  /** Groups degraded by a rate-limited/erroring vendor (VENDOR_UNAVAILABLE) — the
   *  count behind a PARTIAL run + the dialog's "N degraded by a service" line. */
  degradedGroups?: number;
  /** Vendors currently rate-limiting THIS live run — drives the "in progress —
   *  delayed by service Z (rate limit)" banner. Empty unless a run is RUNNING. */
  delayedBy?: string[];
  /** Vendors responding SLOWLY (high latency, not throttled) during THIS live run —
   *  drives the "Z responding slowly" banner. Explains a slow run with no 429s. */
  slowBy?: string[];
  /** Hotels committed so far — climbs as async booking workers settle reservations after
   *  the run is terminal. A push where this changed = a hotel settled → refresh the board. */
  roomsCommitted?: number;
  /**
   * POL-13 — per-group outcome breakdown from the rebooking run's group-states,
   * pushed live over the WS progress. Authoritative + run-synchronous (exact at
   * completion, no async cases-denorm lag) — the progress dialog's metric tiles
   * read these instead of deriving from the paginated roster (no client timer).
   */
  assignedGroups?: number;
  groupsWithOptions?: number;
  noOptionGroups?: number;
  noOptionsByReason?: Record<string, number>;
  computedAt: string;
  /** S16/NEX-313 — the live bulk run, derived server-side from
   * `runStatus === 'RUNNING'` (else null). Truthful: clears once terminal. */
  activeRun?: DisruptionActiveRun | null;
  /** NEX-296 — the rebooking run marker's own runId, for every status (null when no marker). */
  runRunId?: string | null;
  /** QA fix 5 (additive) — ISO-8601 instant the last rebooking run went terminal
   *  (null while live / no marker / legacy marker). Ages the "flight search
   *  completed 78/78" idle line so a weeks-old run never reads as fresh. */
  runCompletedAt?: string | null;
  /**
   * rebooking-progress-transparency (NEX-364 / progress-settle@v2) — the per-PNR
   * settle outcomes carried on each SETTLED group of the live WS progress feed.
   * Additive to the frozen NEX-313 shape; empty until the rebooking run marker
   * carries settle outcomes. Drives the rich per-PNR rows filling in live over the
   * WS as groups settle. Realtime = WS (never polled).
   */
  settledGroups?: RebookingSettleOutcome[];
  /** `airline-money@v2` (D17, additive) — `false`: the price card was unavailable, so the
   *  `adcMinor` of a flight Nexa buys in `settledGroups` is `null` (never the cost). */
  priced?: boolean | null;
  /**
   * The most recent bulk HOTEL-ALLOCATION run (booking's durable marker + the
   * worker's live counters), or null when none ran. The Allocation progress
   * dialog reads THIS — the rebooking `runStatus` above says nothing about hotels.
   */
  allocationRun?: DisruptionAllocationRun | null;
  /** Group-level facts riding the WS push (`{known:false}` when unreadable — never zeros). */
  groups?: DisruptionGroupFacts | { known: { cases: false; booking: false } };
  /** MASS ground transport (additive): the shuttle runs of the disruption's transfer
   *  plan, per hotel — bumped by the BFF run broadcaster on every run/boarding event. */
  transferRun?: DisruptionTransferRun | null;
  /**
   * «Nexa trabajando» (`operator-bff:work-progress@v1`, additive) — the sub-cases the
   * rebooking run marker PLANNED (sorted, de-duplicated), so a pending tile can be
   * labelled before it settles. `null` = a legacy marker; absent = an older BFF.
   */
  runPlannedGroupIds?: string[] | null;
  /** The BFF's synchronous fan-out run (`sync-run@v1`): RUNNING, or the last finished
   *  one for a while (its summary). `null`/absent = none / an older BFF. */
  syncRun?: IDisruptionSyncRun | null;
  /** Automatic meal tiers, per PNR (`care-entitlements.progress@v1`). `known:false` =
   *  the wallet was not read — render «unknown», never zeros. */
  careIssuance?: IDisruptionCareIssuance;
}

/* ── «Nexa trabajando» (docs/verticals/nexa-trabajando.md §7–§9) ─────────────────
 * UI mirror of the FROZEN operator-bff work-progress additions. Every field is
 * optional upstream; absent = unknown, never zero. No PII: URNs, codes, hotel names,
 * flight labels, counts and times only (the UI joins the roster for the PNR label). */

export type TSyncRunKind =
  | 'ticket-repair'
  | 'send-offers'
  | 'force-accept'
  | 'vouchers'
  | 'transfers'
  | 'recovery-search'
  | 'recovery-offer-best';
export type TSyncRunOutcome = 'DONE' | 'ALREADY_DONE' | 'SKIPPED' | 'FAILED';

export interface IDisruptionSyncRunItem {
  /** `null` = a case- or hotel-level item. */
  subCaseUrn: string | null;
  caseUrn?: string;
  hotelUrn?: string;
  outcome: TSyncRunOutcome;
  /** Machine code (`ticket_issued`, `already_ticketed`, `not_accepted`, `forced`…). */
  reason?: string;
  /** Non-PII, ≤ 80 chars: a ticket number, a card URN, "2 run(s)". */
  detail?: string;
  at: string;
}

export interface IDisruptionSyncRun {
  runId: string;
  op: 'tickets' | 'acceptance' | 'vouchers' | 'transfers';
  kind: TSyncRunKind;
  status: 'RUNNING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
  operatorUrn: string;
  startedAt: string;
  finishedAt?: string;
  progress: { done: number; total: number; unit: 'cases' | 'groups' | 'hotels' };
  tally: Record<string, number>;
  /** De-duplicated per sub-case, capped at 1000 (`itemsTotal` says how many there were). */
  items: IDisruptionSyncRunItem[];
  itemsTotal: number;
  rev: number;
  /** Why a run ended FAILED (§7 amendment): `stale` (no tick for 10 min) or `error`. */
  reason?: string;
}

export type CareIssuanceState = 'ENTITLED' | 'IN_FLIGHT' | 'DISPATCHED' | 'LOADED' | 'FAILED';

export interface ICareIssuanceItem {
  subCaseUrn: string;
  state: CareIssuanceState;
  at: string;
  entitledAt: string;
  mode?: 'ISSUED' | 'TOPPED_UP';
  by?: 'SYSTEM' | 'OPERATOR';
  attempts: number;
  failure?: { code: string; retryable: boolean };
  settledAt?: string;
  version: string;
}

export interface ICareIssuanceTier {
  kind: 'CARE_ISSUANCE';
  tierIndex: number;
  reasonCode: string;
  thresholdMinutes?: number;
  counts: { entitled: number; inFlight: number; dispatched: number; loaded: number; failed: number; total: number };
  firstAt?: string;
  lastAt?: string;
  items: ICareIssuanceItem[];
}

export interface IDisruptionCareIssuance {
  known: boolean;
  version: string;
  tiers: ICareIssuanceTier[];
  topUps?: { subCaseUrn: string; reasonCode: string; dispatchedAt?: string; settledAt?: string }[];
}

export type BulkAllocationGroupOutcome =
  | 'HELD'
  | 'SOLD_OUT'
  | 'NO_COMPLIANT_OFFER'
  | 'NO_CAPACITY'
  | 'ALREADY_RESERVED'
  | 'FAILED';

/** One PNR group of a bulk hotel-allocation run (`booking:bulk-allocation.groups@v1`
 *  as the BFF forwards it — no money). `HELD` = soft hold + issue enqueued; the
 *  reservation lands async (its confirmation arrives on `CaseChanged('booking')`). */
export interface IDisruptionAllocationGroup {
  subCaseUrn: string;
  caseUrn: string;
  outcome: BulkAllocationGroupOutcome;
  /** When THIS run settled the group (ISO). */
  at: string;
  hotelUrn?: string;
  hotelName?: string;
  rooms?: number;
  guests?: number;
  nights?: number;
  consolidated?: boolean;
  reservationUrn?: string;
  /** ALREADY_RESERVED only. */
  reservationStatus?: 'PENDING' | 'HELD' | 'BOOKED';
  /** FAILED only — a code-like message, never free text. */
  error?: string;
  /** Folded in from an EARLIER run under a scoped (per-PNR) marker — context, not this
   *  run's work. Optional: absent on a BFF that does not flag the fold. */
  earlierRun?: true;
}

/** Live shuttle-run snapshot riding the progress push (`DisruptionProgress.transferRun`). */
export interface DisruptionTransferRun {
  vehiclesPlanned: number;
  vehiclesOnRoad: number;
  arrivedRuns: number;
  paxArrived: number;
  paxTotal: number;
  byHotel: DisruptionTransferRunHotel[];
}

export interface DisruptionTransferRunHotel {
  hotelUrn: string;
  hotelName?: string;
  paxToMove: number;
  paxArrived: number;
  runs: {
    planned: number;
    dispatched: number;
    boarding: number;
    inTransit: number;
    arrived: number;
    failed: number;
  };
  /** ISO-8601 — the next pickup at this hotel's airport stand (planned/boarding run). */
  nextPickupAt?: string;
}

/** Booking's bulk-allocation run marker as the BFF progress feed carries it. */
export interface DisruptionAllocationRun {
  runId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  operatorUrn: string | null;
  startedAt: string | null;
  updatedAt: string | null;
  casesTotal: number;
  casesProcessed: number;
  totalGroups: number;
  processedGroups: number;
  heldGroups: number;
  soldOutGroups: number;
  noOfferGroups: number;
  alreadyReservedGroups: number;
  /** Per PNR group, sorted by `at` (additive, `bulk-allocation.groups@v1`); `[]` on a
   *  legacy marker, absent on an older BFF. The counters above keep their meaning. */
  groups?: IDisruptionAllocationGroup[];
  /** A per-PNR (drawer) run (§4 amendment, additive; absent on an older BFF). */
  scoped?: boolean;
  /** A scoped run's PNRs, sorted — so every viewer can label its pending tiles. */
  plannedSubCaseUrns?: string[];
}

/* ── rebooking-progress-transparency (NEX-364 / U1) ───────────────────────────
 * UI mirror of the FROZEN operator-bff rebooking wire contract (slice B1,
 * apps/operator-bff/src/modules/rebooking/rebooking.types.ts). Kept
 * byte-compatible; do NOT import from operator-bff. PII is mask-by-default —
 * `pnrMasked` is already masked upstream; never attempt to unmask it. */

/** Option/strategy kinds the rebooking engine produces (`best.kind` / row `kind`). */
export type RebookingOptionKind =
  | 'OWN_FLIGHT'
  | 'INTERLINE'
  | 'OWN_WITH_LAYOVER'
  | 'HOTEL_THEN_FLIGHT'
  | 'SPLIT'
  | 'AIRLINE_EXTERNAL';

/** The rank-1 (winning) option summary for a group — the "why" behind the decision. */
export interface RebookingBestOption {
  kind: string;
  rank: number;
  /** Additional cost of change (fare diff) in minor units (cents). commercial-contract D20:
   *  on the airline's own option it is the AIRLINE's reissue cost / fare difference, as is;
   *  on a flight Nexa buys (Duffel / Atlas) it is the FLIGHT price — `null` without a price
   *  card (D17, `priced: false`), which renders «No disponible», never 0. */
  adcMinor: number | null;
  /** Onward-connection feasibility (connection-aware). */
  onwardFeasible: boolean;
  /** Whether the option incurs an overnight (drives a hotel). */
  overnight: boolean;
  /** Governing `policy.rebooking` URN the option was offered under (POL-11). */
  policyUrn?: string;
}

/** Rank-1 option's score components (MVP dialog's score bars). `airline-money@v2`: the
 *  option's mixed total (Nexa's cost) is no longer sent — scores only. */
export interface RebookingScored {
  urgency: number;
  satisfaction: number;
  finalScore: number;
}

/** One strategy (option) row for the detail panel's "N strategies" list — sorted by rank. */
export interface RebookingOptionRow {
  optionUrn: string;
  kind: string;
  status: string;
  rank: number;
  /** Friendly flight label — "LA512 SCL→LIM". Not PII. */
  flightLabel: string;
  /** Additional cost of change (fare diff) in minor units — as {@link RebookingBestOption.adcMinor}. */
  adcMinor: number | null;
  finalScore: number;
  /** Governing `policy.rebooking` URN the option was offered under (POL-11). */
  policyUrn?: string;
  /** QA fix 5 (additive) — the option's first segment already departed (read-seam
   *  soft mark): never render it as a fresh candidate. */
  expired?: boolean;
}

/** The disrupted (original) itinerary for the dialog's "Original itinerary" card. */
export interface RebookingOriginalItinerary {
  flightLabel: string;
  originIata: string;
  destIata: string;
  cabin?: string;
  status?: string;
}

/** How the run that last settled a group ended it (`rebooking:progress-settle@v3`). */
export type RebookingSettleKind = 'generated' | 'no-route' | 'preserved' | 'degraded';

/** The run's auto-accept verdict for a group (`progress-settle@v3`). */
export type RebookingAcceptVerdict =
  | 'ASSIGNED'
  | 'NO_FEASIBLE_OPTION'
  | 'SKIPPED_BY_CAP'
  | 'NO_SEATS'
  | 'ERROR'
  | 'NOT_AUTO';

/** The flight a SELECTED group holds — its rank may exceed `best.rank` after a capacity spill. */
export interface RebookingAssignedFlight {
  optionUrn: string;
  kind: string;
  rank: number;
  /** "LA834 (SCL→CLO)" — not PII. */
  flightLabel: string;
  departureAt?: string;
  arrivalAt?: string;
  selectedAt?: string;
  ticketed?: true;
}

/** `progress-settle@v3` — additive per-PNR facts of the run that last settled a group. */
export interface RebookingSettleFacts {
  /** ISO — when the run settled it (a retry re-stamps it). */
  settledAt?: string | undefined;
  settleOutcome?: RebookingSettleKind | undefined;
  accept?: { verdict: RebookingAcceptVerdict; at: string } | undefined;
  /** Only when `status === 'SELECTED'`. */
  assigned?: RebookingAssignedFlight | undefined;
  /** The flight providers the group's latest search asked and why each did or did not
   *  answer (contract `airline-air-ticketing.v1` §3) — absent when none was asked. */
  providerAttempts?: FlightProviderAttempt[] | undefined;
}

/** One group's rebooking outcome at the DISRUPTION grain (A1 group-results row). */
export interface RebookingGroupResult extends RebookingSettleFacts {
  groupId: string;
  /** Mask-by-default PNR label (e.g. `A*****`) — never the raw locator. */
  pnrMasked: string;
  paxCount: number;
  status: RosterRebookingStatus | string;
  /** Coded reason when `status === NO_OPTIONS` / `NOT_APPLICABLE`. */
  noOptionsReason?: string | undefined;
  best?: RebookingBestOption | undefined;
  scored?: RebookingScored | undefined;
  /** ALL options (strategies), sorted by rank asc. Empty when none. */
  options: RebookingOptionRow[];
  original?: RebookingOriginalItinerary | undefined;
  /** How many LIVE (not departed) options the group has. */
  optionCount: number;
  /** QA fix 5 (additive) — the group HAD options but every one already departed:
   *  render "Expired — search again", never "Recommended #1" / "5 options". */
  optionsExpired?: boolean | undefined;
}

/** operator-bff:GET /v1/disruptions/{urn}/rebooking-detail@v1 response — the
 *  one-shot open read; live per-PNR settle outcomes arrive over the WS thereafter. */
export interface RebookingDetailView {
  urn: string;
  groups: RebookingGroupResult[];
  computedAt: string;
  /** D17 — `false`: the price card was unavailable and a Nexa-bought `adcMinor` is `null`. */
  priced?: boolean;
}

/** progress-settle@v2 — the per-PNR outcome carried on each SETTLED group of the
 *  live WS progress feed (`disruption-progress@v2`); v3 adds the settle facts. */
export interface RebookingSettleOutcome extends RebookingSettleFacts {
  groupId: string;
  /** Mask-by-default PNR label — never the raw locator. */
  pnrMasked: string;
  paxCount?: number | undefined;
  status: RosterRebookingStatus | string;
  noOptionsReason?: string | undefined;
  best?: RebookingBestOption | undefined;
  scored?: RebookingScored | undefined;
  options: RebookingOptionRow[];
  original?: RebookingOriginalItinerary | undefined;
  /** QA fix 5 (additive) — every option of the group already departed. */
  optionsExpired?: boolean | undefined;
}

/* ── S16 (NEX-292) — disruption bulk-lock 409 ────────────────────────────────── */

/** The BFF's `bulk_in_progress` 409 body — another operator holds the bulk lock
 * for this disruption+op. The bar shows a toast + disables until the run clears. */
export interface BulkInProgress409 {
  code: 'bulk_in_progress';
  op: 'rebooking' | 'allocation';
  /** The holding operator (URN). */
  inProgressBy: string;
  /** When their run started (ISO). */
  since: string;
}

/** Thrown by the bulk triggers when the disruption lock is held (409). Carries
 * the holder so the bar can show "Already running — started by {inProgressBy}". */
/**
 * A disruption fan-out refused because another fan-out of the same disruption is RUNNING
 * (operator-bff `sync-run@v1`: 409 `{ error: 'sync_run_in_progress', runId, kind, startedAt }`).
 */
export class SyncRunInProgressError extends Error {
  readonly runId: string;
  readonly kind: string;
  readonly startedAt: string;
  constructor(body: { runId?: unknown; kind?: unknown; startedAt?: unknown }) {
    super('another run is already in progress for this disruption');
    this.name = 'SyncRunInProgressError';
    this.runId = typeof body.runId === 'string' ? body.runId : '';
    this.kind = typeof body.kind === 'string' ? body.kind : '';
    this.startedAt = typeof body.startedAt === 'string' ? body.startedAt : '';
  }
}

export class BulkInProgressError extends Error {
  readonly op: 'rebooking' | 'allocation';
  readonly inProgressBy: string;
  readonly since: string;
  constructor(body: BulkInProgress409) {
    super(`bulk ${body.op} already running (held by ${body.inProgressBy})`);
    this.name = 'BulkInProgressError';
    this.op = body.op;
    this.inProgressBy = body.inProgressBy;
    this.since = body.since;
  }
}

/** One per-flight price row (GET /v1/disruptions/:urn/cost-breakdown — `airline-money@v2`:
 *  the route keeps its path, every amount is the airline's PRICE; null = not available). */
export interface DisruptionPriceFlightRow {
  flightLabel: string;
  caseUrn: string;
  flightUrn?: string;
  airportCode: string;
  passengerCount: number;
  priceMinor: number | null;
  bookedPriceMinor?: number | null;
  heldPriceMinor?: number | null;
  estimatedPriceMinor?: number | null;
}

/** Price breakdown (GET /v1/disruptions/:urn/cost-breakdown, `airline-money@v2`). */
export interface DisruptionPriceBreakdown {
  urn: string;
  totalPriceMinor: number | null;
  currency: string;
  flights: DisruptionPriceFlightRow[];
  flightsTotalMinor: number | null;
  reconciled: boolean;
  computedAt: string;
  bookedPriceMinor?: number | null;
  heldPriceMinor?: number | null;
  estimatedPriceMinor?: number | null;
  priceSource?: 'actual+estimate' | 'estimate';
  /** false ⇒ every amount is null («No disponible»). Optional: older BFF. */
  priced?: boolean;
}

/** One service line of a disruption's spend (GET /v1/disruptions/:urn/spend@v2). Money in
 *  minor units, always the PRICE the airline pays (commercial-contract P2 — Nexa's cost never
 *  reaches the browser); `null` when the price could not be computed («No disponible»). */
export interface IDisruptionSpendLine {
  serviceType: 'FLIGHT' | 'HOTEL' | 'TRANSFER' | 'MEAL';
  currency: string;
  shownMinor: number | null;
  basis: 'ACTUAL' | 'ESTIMATE' | 'MIXED' | 'NONE';
  /** The line's source answered; `false` = unknown, not zero. */
  known: boolean;
  /** Keys ending in `Minor` are priced money (null when unpriced); the rest are counts. */
  detail: Record<string, number | null>;
}

/** The disruption's total committed spend as the airline pays it (GET /v1/disruptions/:urn/spend@v2, `costs.view`). */
export interface IDisruptionTotalSpend {
  disruptionUrn: string;
  currency: string;
  shownTotalMinor: number | null;
  /** The estimated part of the total (flight quotes, hotel groups still without a room), priced. */
  shownEstimateMinor: number | null;
  complete: boolean;
  /** False when the commercial contract's price could not be read: every amount is null. */
  priced: boolean;
  lines: IDisruptionSpendLine[];
  otherCurrencies: Array<{ serviceType: IDisruptionSpendLine['serviceType']; currency: string; shownMinor: number | null }>;
  computedAt: string;
}

/* ── S6 (NEX-281) — bulk-command trigger results ─────────────────────────────── */

/** 202 result of the bulk auto-rebook trigger (POST …/auto-rebook). */
export interface BulkAutoRebookResult {
  queuedCases: number;
  queuedGroups: number;
  /** Correlates with the Progress tab poll. */
  runId: string;
}

/** 202 result of the bulk allocate trigger (POST …/allocate). */
export interface BulkAllocateResult {
  queuedCases: number;
  /** Correlates with the Progress tab poll. */
  runId: string;
}

/** Result of the meal-voucher fan-out (POST …/vouchers/issue-all). The BFF issues
 *  one wallet card per not-yet-issued MEAL_VOUCHER group; each emits card-issued →
 *  cases flips the roster chip to "Voucher issued". */
export interface IssueVouchersResult {
  issued: number;
  skipped: number;
  failed: Array<{ subCaseUrn: string; error: string }>;
}

/**
 * Result of the per-PNR meal-voucher issue (POST …/pnrs/:subCaseUrn/vouchers/issue),
 * the roster drawer's MealVoucherCard. `issued` true when a card was issued this call;
 * `skipped` true when a guard short-circuited (not a meal-voucher group, already
 * issued, no KYC, offline) with `reason` saying which. A wallet non-2xx is
 * `issued:false` (not a thrown error) with the upstream status in `reason`. `cardUrn`
 * is present on a successful issue. The card flips to "Voucher issued" once the async
 * `wallet.card.issued` → cases denorm lands (hence the +4s re-invalidate in the hook).
 */
export interface IssueGroupVoucherResult {
  subCaseUrn: string;
  issued: boolean;
  skipped: boolean;
  reason?: string;
  cardUrn?: string;
}

/** Operator-facing priority chips (drive the `priority` query param). */
export const ROSTER_PRIORITY_TAGS = [
  'UMNR',
  'VIP',
  'WCHR',
  'MEDA',
  'BUSINESS',
  'FIRST',
  'ONWARD_AT_RISK',
] as const;
export type RosterPriorityTag = (typeof ROSTER_PRIORITY_TAGS)[number];

export const ROSTER_REBOOKING_STATUSES: readonly RosterRebookingStatus[] = [
  'PENDING',
  'OPTIONS_READY',
  'SELECTED',
  'OVERRIDDEN',
  'NO_OPTIONS',
  'DEFERRED',
  'NOT_APPLICABLE',
];

/* ── disruption-overview (epic NEX-376) — resolution-state@v1 ──────────────────
 * UI mirror of the FROZEN operator-bff seam (slice B1,
 * apps/operator-bff/src/modules/disruptions/resolution-state.types.ts). Kept
 * byte-compatible; do NOT import from operator-bff (no cross-app compile dep).
 * If the frozen seam moves, update both. Mask-by-default PII — this shape carries
 * only counts + labels, never a raw passenger record.
 *
 * GET /v1/disruptions/{urn}/resolution-state → 200 ResolutionState */

/** Rebooking run lifecycle — mirror of the progress snapshot's `runStatus`. */
export type ResolutionRunStatus = RunStatus;

/** The ordered resolution operations the cockpit walks the operator through.
 *  `transfers` (MASS ground transport — "Send to hotels") is additive: an older
 *  BFF never emits it, and every consumer tolerates its absence. */
export type ResolutionOp =
  | 'rebooking'
  | 'tickets'
  | 'hotels'
  | 'vouchers'
  | 'acceptance'
  | 'transfers'
  | 'contain';

/** Per-step lifecycle in the cockpit checklist. */
export type ResolutionStepStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'DONE'
  | 'PARTIAL'
  | 'BLOCKED'
  | 'SKIPPED'
  | 'UNKNOWN';

/** Headline counters — the KPI tiles (mirror of the Overview rollup). */
export interface ResolutionKpis {
  caseCount: number;
  affectedPassengers: number;
  rebookedPassengers: number;
  hotelsIssued: number;
  /** Group-level facts (the guided resolution's counters); absent when unreadable. */
  groups?: DisruptionGroupFacts;
  /** `airline-money@v2` — hotel spend AS THE AIRLINE PAYS IT: booked + held (real
   *  reservations) + est. for hotel groups still without one, priced server-side;
   *  null = not available («No disponible»). */
  totalPriceMinor: number | null;
  currency: string;
  bookedPriceMinor?: number | null;
  heldPriceMinor?: number | null;
  estimatedPriceMinor?: number | null;
  priceSource?: 'actual+estimate' | 'estimate';
  /** MASS ground transport (additive; absent on an older BFF): the shuttle facts
   *  of the disruption's transfer plan — see `ITransferFacts`. */
  transfers?: ITransferFacts;
  /** Σ completed shuttle runs, priced (actual only, minor units; additive); null = not available. */
  transportPriceMinor?: number | null;
}

/** The active bulk run (present only while a run is RUNNING). */
export interface ResolutionActiveRun {
  op: string;
  runId: string;
  operatorUrn: string;
  startedAt: string;
  progress?: { done: number; total: number; unit: 'cases' | 'groups' };
}

/** Live run progress — the smooth bar + counters. */
export interface ResolutionProgress {
  runStatus: ResolutionRunStatus;
  activeRun?: ResolutionActiveRun;
  processedCases: number;
  totalCases: number;
  processedGroups: number;
  plannedGroups: number;
  /** QA fix 5 (additive) — when the LAST rebooking run went terminal (ISO-8601).
   *  Absent on older markers / while a run is live. */
  lastRunCompletedAt?: string;
}

/** One row of the ordered cockpit checklist. */
export interface ResolutionStep {
  op: ResolutionOp;
  status: ResolutionStepStatus;
  /** null = unknown (source unreadable) — never invented. */
  done: number | null;
  total: number | null;
  remaining?: number;
  gaps?: number;
  actionable?: boolean;
  reason?: string;
  requiresRole?: 'ops_supervisor';
  source?: 'facts' | 'marker' | 'unknown';
  /** `contain` only — NATURAL (all travelled/declined) | TRAVELLING (tickets issued, waiting) | FORCED (supervisor may force). */
  containMode?: 'NATURAL' | 'TRAVELLING' | 'FORCED';
  /** Acceptance bundle: the step runs by itself once the passenger (or a
   *  supervisor) accepts — `tickets` = "issue ticket + confirm hotel", never a
   *  button. Absent on an older BFF. */
  automatic?: boolean;
  /** rebooking only: the residual (options ready · no seat taken by the auto-select)
   *  is MANUAL work per PNR — the flow walks on to hotels; the retry stays actionable
   *  on the step itself (fired explicitly via resolve-next expectedOp). */
  residualManual?: boolean;
  /** QA fix 4 (additive) — a dedicated REPAIR action behind this step's CTA, NOT
   *  the resolve-next op: `send-offers` re-opens the acceptance gate for complete
   *  offers a missed event left behind. Absent on an older BFF. */
  repair?: 'send-offers';
  /** rebooking only (additive) — the step is SKIPPED because the station's governing
   *  policy carries no rebooking block (rebooking TDS §18 "opt-in by absence"): the
   *  engine is off here, so the flow walks straight to hotels and care. */
  policyDisabled?: boolean;
  /** With `policyDisabled` — the governing (disabling) policy, for the "view policy" link. */
  policyUrn?: string;
  /** rebooking only (additive, `operator-bff:IResolutionStep.heldByDelay@v1`, unified-declare
   *  B2): SKIPPED because the flight-delay hold keeps the passengers on their own delayed
   *  flight (`flight-delay.tds.md` §5.1) — settled for the walk, but ON HOLD, not done: it
   *  lifts when the flight departs or a supervisor releases the case. Absent on an older BFF. */
  heldByDelay?: boolean;
  /** hotels only (same seam; narrowed by `delayHotel@v1`, delay-limits B3): SKIPPED because
   *  every group carries the flight-delay meals-only stamp (§5.2) — the policy's next-day
   *  hotel rule (new departure on the next local day at or after the set hour) gave none
   *  of them a hotel: this delay gets meals only. Never set together with `delayHotel`.
   *  Absent on an older BFF. */
  delayCareOnly?: boolean;
  /** hotels only (additive, `operator-bff:IResolutionStep.delayHotel@v1`, delay-limits B3):
   *  the policy's next-day hotel rule gave some delay group a hotel — the step is the
   *  ordinary facts-based one; the marker says why a delay has it. Absent on an older BFF. */
  delayHotel?: true;
}

/** A vendor responding slowly on the live run. */
export interface ResolutionSlowVendor {
  vendor: string;
  avgLatencyMs: number;
}

/** The "problems / attention" section — everything blocking a clean resolution. */
export interface ResolutionProblems {
  noOptionsByReason: Record<string, number>;
  degradedGroups: number;
  delayedBy: string[];
  slowBy: ResolutionSlowVendor[];
  allocationGaps: number;
  awaitingAcceptance: number;
  declined?: number;
  unticketed?: number;
  holdsWithoutOffer?: number;
  /** Acceptance bundle (additive): offer-ready groups whose offer has not gone
   *  out yet; accepted groups whose ticket/hotel commit is still in flight; and
   *  commits stale beyond the BFF threshold (repair). */
  offersNotSent?: number;
  acceptedNotIssued?: number;
  commitFailed?: number;
  /** MASS ground transport (additive): passengers left behind at a pickup, runs the
   *  vendor failed, ticketed+hotel groups with no seat after the BFF threshold, and
   *  hotels whose return run to the airport is not scheduled yet. */
  transferNoShow?: number;
  transferFailed?: number;
  transferLate?: number;
  returnUnscheduled?: number;
}

/** The "implications" section — what confirming everything commits, at the airline's
 *  price (`airline-money@v2`; null = not available). */
export interface ResolutionImplications {
  estimatedPriceMinor: number | null;
  currency: string;
  /** Reversible HELD/SELECTED groups (not yet confirmed). */
  heldCount: number;
  /** Confirmed/committed groups. */
  confirmedCount: number;
}

/** The single recommended next action (first non-DONE step, or 'none' when done). */
export interface ResolutionNextStep {
  op: ResolutionOp | 'none';
  label: string;
  reason: string;
  mode?: 'start' | 'retry' | 'wait' | 'blocked' | 'unknown' | 'done';
  actionable?: boolean;
  /** Button label with the concrete count. */
  cta?: string;
  requiresRole?: 'ops_supervisor';
}

/** The full `resolution-state@v1` read-aggregate. */
export interface ResolutionState {
  urn: string;
  kpis: ResolutionKpis;
  progress: ResolutionProgress;
  steps: ResolutionStep[];
  problems: ResolutionProblems;
  implications: ResolutionImplications;
  nextStep: ResolutionNextStep;
  /** `airline-money@v2` — false ⇒ every amount in `kpis` / `implications` is null. */
  priced?: boolean;
  computedAt: string;
}

/* ── disruption-overview (epic NEX-376 / U3) — resolve-next@v1 one-click seam ─────
 * UI MIRROR of the FROZEN operator-bff `resolve-next@v1` result
 * (apps/operator-bff/src/modules/disruptions/resolve-next.types.ts) — byte-
 * compatible. Do NOT import from operator-bff (no cross-app compile dep); if the
 * frozen seam moves, update both. The op union is identical to `ResolutionOp`,
 * reused here to stay DRY. Counts/labels only — never a raw passenger record. */

/** One residual checklist row — an op not yet DONE (mirror of the seam). */
export interface ResolveNextRemainingStep {
  op: ResolutionOp;
  status: string;
}

/**
 * The `resolve-next@v1` result. `startedOp` is what fired (or WOULD fire on a
 * `dryRun`, or 'none' when nothing is left / a run already holds the disruption
 * lock). `runId` is present only when an async op (rebooking / hotels) was queued.
 * `reason` mirrors resolution-state.nextStep.reason (or the lock reason). STOPS AT
 * HELD — there is deliberately no confirm/issue op in this set.
 */
export interface ResolveNextResult {
  startedOp: ResolutionOp | 'none';
  runId?: string;
  reason: string;
  remainingSteps: ResolveNextRemainingStep[];
  dryRun?: boolean;
  tally?: Record<string, number>;
  requiresRole?: 'ops_supervisor';
  nextStep?: { op: ResolutionOp | 'none'; reason: string; mode?: string; cta?: string; actionable?: boolean };
}

/* ── MASS ground transport ("Send to hotels") — transfer board seam ─────────────
 * UI mirror of the FROZEN operator-bff seam (docs/verticals/transport.md §7 ·
 * `transport:transfer-plans@v1` · `resolution-state@v1+transfers`). Everything is
 * ADDITIVE and OPTIONAL: an older BFF answers 404 on the board route and never emits
 * the `transfers` op — the Tower then draws the Transfers gate as "not wired" and
 * keeps the classic six-op picture. Counts + labels + a masked hotel name only —
 * never a passenger record (the run manifest stays in the transport domain).
 *
 * GET  /v1/disruptions/{urn}/transfers            → ITransferBoard
 * POST /v1/disruptions/{urn}/transfers/plan       {vehicleClasses?, hotelUrn?} → ITransferBoard
 * POST /v1/disruptions/{urn}/transfers/dispatch   {hotelUrn?, runUrns?, reason} → 202 {runs}
 * POST /v1/transport/runs/{urn}/{cancel|board|no-show|own-means|replace-no-shows}
 * WS (disruption room): TransferRunChanged {disruptionUrn, run} · TransferBoardChanged {disruptionUrn, plan} */

/**
 * The transfer facts of `resolution-state@v1` (`kpis.transfers`). UNITS — the plan
 * speaks PASSENGERS, runs are VEHICLES:
 *  - passengers (outbound leg): `needed` (non-infant pax of the groups whose ticket
 *    is issued AND hotel confirmed — the demand), `enrolled` (seats in the plan),
 *    `planned` (seats cut into a run), `dispatched` (seats on runs the vendor was
 *    asked for — REQUESTED…ARRIVED, cumulative), `enRoute` (boarded seats on
 *    BOARDING|DEPARTED runs), `arrived` / `noShow` / `ownMeans` (seat outcomes);
 *  - vehicles: `failed` (FAILED outbound runs), `vehiclesPlanned` (outbound runs
 *    still PLANNED — cut, not dispatched), `vehiclesOnRoad` (REQUESTED…DEPARTED),
 *    `returnScheduled` / `returnOnRoad` / `returnDone` (return-leg runs);
 *  - `hotels` = hotels in the plan (or with demand); `known:false` = transport unreadable.
 */
export interface ITransferFacts {
  needed: number;
  enrolled: number;
  planned: number;
  dispatched: number;
  enRoute: number;
  arrived: number;
  noShow: number;
  ownMeans: number;
  failed: number;
  hotels: number;
  vehiclesPlanned: number;
  vehiclesOnRoad: number;
  returnScheduled: number;
  returnOnRoad: number;
  returnDone: number;
  known: boolean;
  /**
   * ADDITIVE (transport-desk T2) — `needed < plan.massThresholdPax`: the
   * disruption is small enough for per-passenger cars (dispatch-cars) instead of
   * a shuttle wave. Absent = threshold unknown (no plan, or an older producer).
   */
  belowThreshold?: boolean;
}

/** Shuttle legs — two-valued ON PURPOSE: runs are hotel-keyed; a ride home (`AIRPORT_TO_HOME`) is never a run. */
export type TransferLeg = 'AIRPORT_TO_HOTEL' | 'HOTEL_TO_AIRPORT';

/** transport-tds §3.2 — the shuttle-run FSM. */
export type ShuttleRunStatus =
  | 'PLANNED'
  | 'REQUESTED'
  | 'VEHICLE_ASSIGNED'
  | 'BOARDING'
  | 'DEPARTED'
  | 'ARRIVED'
  | 'CANCELLED'
  | 'FAILED';

export interface TransferVehicleClass {
  code: string;
  capacity: number;
}

export interface TransferPickupPoint {
  label: string;
  geo?: { lat: number; lon: number };
}

/** `ShuttleRunView@v1` — one vehicle run of the plan (a bus / van / car). */
export interface ShuttleRunView {
  urn: string;
  planUrn: string;
  disruptionUrn: string;
  caseUrns: string[];
  hotelUrn: string;
  hotelName?: string;
  leg: TransferLeg;
  /** Run number within the hotel + leg (the passenger's "Shuttle #3"). */
  seq: number;
  wave: number;
  vehicleClass: string;
  capacity: number;
  assignedCount: number;
  boardedCount: number;
  noShowCount: number;
  arrivedCount: number;
  status: ShuttleRunStatus;
  /** ISO-8601 — the pickup time. */
  scheduledAt: string;
  pickupPoint: TransferPickupPoint;
  dropoff?: TransferPickupPoint;
  providerName: string;
  vehicle?: { class: string; capacity: number; plate: string; model?: string };
  driverInfo?: { name?: string; vehicle?: string; licensePlate?: string };
  dispatchedAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  /** `airline-money@v2` (P5): what the airline pays for the run (was `cost`) — absent until
   *  the vendor writes an amount, null when it could not be priced («No disponible»). */
  price?: { amount: number; currency: string } | null;
  /** false ⇒ the price card was unavailable (D17). Optional: an older BFF omits it. */
  priced?: boolean;
  lastError?: string;
  updatedAt: string;
}

/** Seat counters of a plan hotel (per leg or flat — the mirror tolerates both). */
export interface TransferSeatCounters {
  pending: number;
  assigned: number;
  boarded: number;
  arrived: number;
  noShow: number;
  ownMeans: number;
}
export interface TransferRunCounters {
  planned: number;
  active: number;
  arrived: number;
  failed: number;
}
export type TransferLegCounters<T> = T | Partial<Record<TransferLeg, T>>;

/** One hotel of `TransferPlanView@v1`. */
export interface TransferPlanHotelView {
  hotelUrn: string;
  hotelName?: string;
  pickupPoint?: TransferPickupPoint;
  seats?: TransferLegCounters<TransferSeatCounters>;
  runs?: TransferLegCounters<TransferRunCounters>;
  nextPickupAt?: string;
}

/** `TransferPlanView@v1` — the disruption's transfer plan (one per disruption). */
export interface TransferPlanView {
  urn: string;
  disruptionUrn: string;
  status: string;
  vehicleClasses: TransferVehicleClass[];
  hotels: TransferPlanHotelView[];
  totals?: {
    seats?: TransferLegCounters<TransferSeatCounters>;
    runs?: TransferLegCounters<TransferRunCounters>;
  };
  version: number;
  updatedAt: string;
  /** ADDITIVE (transport-desk T1) — the tenant's mass-transport threshold in
   *  passengers. Below it the desk offers per-passenger cars instead of a
   *  shuttle wave. Absent from older producers. */
  massThresholdPax?: number;
}

/** One hotel row of the board: the roster's need + the plan's counters. */
export interface TransferBoardHotel extends TransferPlanHotelView {
  hotelUrn: string;
  hotelName: string;
  /** Passengers who hold a ticket + this hotel and must be moved (roster). */
  paxNeeded: number;
  /** PNR groups behind `paxNeeded`. */
  pnrs: number;
}

/** `GET /v1/disruptions/{urn}/transfers` — the Tower's hotels view + NOW card read it. */
export interface ITransferBoard {
  disruptionUrn?: string;
  plan: TransferPlanView | null;
  runs: ShuttleRunView[];
  hotels: TransferBoardHotel[];
  facts: ITransferFacts;
  /** `transport` = the plan/runs read answered (a 404 "no plan yet" IS known); `roster` =
   *  cases answered. A plain boolean is tolerated (earlier draft of the seam). */
  known: boolean | { transport: boolean; roster: boolean };
  /** Where the plan came from (fixtures = the BFF's in-memory board for local runs). */
  source?: 'transport' | 'fixtures';
  /** `airline-money@v2` (P5) — false ⇒ every run `price` is null. Optional: older BFF. */
  priced?: boolean;
}

/** Whether the board's transport read answered (tolerates both `known` shapes). */
export function transferBoardKnown(board: Pick<ITransferBoard, 'known'> | null | undefined): boolean {
  if (!board) return false;
  return typeof board.known === 'boolean' ? board.known : board.known.transport;
}

export interface PlanTransfersInput {
  vehicleClasses?: TransferVehicleClass[];
  hotelUrn?: string;
}

export interface DispatchTransfersInput {
  hotelUrn?: string;
  runUrns?: string[];
  reason: string;
}

export interface DispatchTransfersResult {
  runs: ShuttleRunView[];
}

/** Per-run operator actions (`POST /v1/transport/runs/{urn}/{action}`). */
export type TransferRunAction = 'cancel' | 'board' | 'no-show' | 'own-means' | 'replace-no-shows';

/* ── feat/care-desk K5 (NEX-457) — care board + care writes ─────────────────────
 * UI mirror of the FROZEN operator-bff care wire contract (slice K4,
 * apps/operator-bff/src/modules/care/care.types.ts). Kept byte-compatible; do
 * NOT import from operator-bff. Money is MINOR units always; a balance the BFF
 * could not read is ABSENT, never a fabricated zero. Realtime = WS
 * (`CareChanged`), never a poll. */

/** Terminal counter outcomes (cases `care-outcome` seam, K3 — frozen §5). */
export type CareOutcomeKind = 'COUNTER_VOUCHER' | 'HANDLED_OUTSIDE';

/** Stamped, overwritable care outcome riding the roster row (cases K3 denorm). */
export interface CareOutcome {
  outcome: CareOutcomeKind;
  note?: string;
  decidedByUrn?: string;
  /** Display name of the declaring operator (#813, additive). */
  decidedByName?: string;
  decidedAt?: string;
}

/** AIRPORT_WAIT meal-grant suggestion (cases K3) — a SUGGESTION only. */
export interface CareSuggestedGrant {
  amount: number;
  currency: string;
  reasonCode: string;
}

/** Frozen top-up reason codes shared wallet↔BFF↔UI (care-desk.md §4.6). */
export type CareReasonCode =
  | 'SPECIAL_NEED'
  | 'AIRPORT_WAIT_MEAL'
  | 'DELAY_EXTENSION'
  | 'ACCESSIBILITY'
  | 'GOODWILL'
  | 'ERROR_CORRECTION';

/**
 * One care card row of the board — per entitled PNR group, whether or not a
 * wallet card exists yet. `status` is the wallet status VERBATIM when a card
 * exists (`ACTIVE|PENDING|FAILED|DISABLED|BLOCKED|…`); the BFF adds three
 * no-card states: `NOT_ISSUED`, `BLOCKED_KYC` (lead has no KYC token — blocks
 * settlement) and `ISSUED` (degraded: card exists, balances not read).
 * `faceValueMinor` is the frozen initial load; `toppedUpMinor` the SETTLED
 * top-up total (separate columns, never merged); `availableMinor` is absent
 * whenever balances were not read. `pendingTopUp` = a top-up was dispatched and
 * its settle has not been observed yet (the "settling" chip).
 */
/** Benefit state of the group's care (#808): no card / live card / consumed / voided. */
export type CareBenefitState = 'NONE' | 'ISSUED' | 'REDEEMED' | 'VOIDED';

/** The wallet's redemption record (#808) — `OPERATOR_SIMULATED` = the counter
 *  marked the coupon used; `ISSUER` = a real clearing (definitive). */
export interface CareRedemption {
  reference: string;
  source: 'OPERATOR_SIMULATED' | 'ISSUER';
  amount?: number;
  currency?: string;
  redeemedAt: string;
  redeemedBy: string;
  note?: string;
  issuerConfirmedAt?: string;
}

export interface CareCardRow {
  subCaseUrn: string;
  pnrLabel: string;
  /** The PRINTED coupon reference (#808) — what the counter keys to validate a
   *  paper coupon against this card. Absent when no card exists yet. */
  reference?: string;
  /** Benefit state (#808). Absent only on boards served by a BFF that predates it. */
  benefit?: CareBenefitState;
  redemption?: CareRedemption;
  /** Lead passenger, masked (PII mask-by-default). */
  holderMasked: string;
  last4?: string;
  /** Card network as the issuer reported it (`MASTERCARD` | `VISA`). Absent
   *  before issuance — the card face must not guess a brand it was not told. */
  network?: string;
  status: string;
  currency: string;
  faceValueMinor: number;
  toppedUpMinor: number;
  availableMinor?: number;
  pendingTopUp?: boolean;
  issuedAt?: string;
  /** Settled spend at a merchant, oldest first (additive — absent on older BFFs / no spend). */
  spend?: Array<{ merchant: string; amountMinor: number; settledAt?: string }>;
}

/**
 * One wallet card of a STANDALONE case (`GET /v1/cases/:caseUrn/care/cards`).
 * The care board is disruption-scoped, so a case outside any disruption reads
 * its cards here — same minor-unit money convention as `CareCardRow`.
 */
export interface CaseCareCard {
  subCaseUrn: string;
  cardUrn: string;
  status: string;
  currency: string;
  faceValueMinor: number;
  toppedUpMinor?: number;
  availableMinor?: number;
  last4?: string;
  network?: string;
  issuedAt?: string;
}

/** Per-currency money row — currencies are NEVER summed across. */
export interface CarePerCurrency {
  currency: string;
  entitledMinor: number;
  onCardsMinor: number;
  availableMinor: number;
  spentMinor: number;
}

export interface CareFacts {
  entitledPnrs: number;
  entitledPax: number;
  issued: number;
  failed: number;
  blockedKyc: number;
  outside: number;
  perCurrency: CarePerCurrency[];
  /** Where the cards were spent, most cards first — `cards` = distinct cards that spent
   *  there (additive; absent on older BFFs). Per currency, never summed across. */
  merchants?: Array<{ merchant: string; currency: string; cards: number; amountMinor: number }>;
}

/** Effective policy care knobs on the board (K2 seam) — presets + cap come
 *  FROM POLICY; the UI keeps local constants only as the fallback for boards
 *  that predate the field. */
export interface CareKnobs {
  topUpPresets: Array<{ label: string; amount: number }>;
  topUpCapPerPnrDay: number;
  currency: string;
}

/** `POST …/pnrs/:sub/care/redeem` → 200 (#808). */
export interface CareRedeemResult {
  redeemed: true;
  subCaseUrn: string;
  redemption: CareRedemption;
  benefit: CareBenefitState;
}

/** `GET …/care/lookup?reference=` (#808) — the card rows whose printed reference matches. */
export interface CareLookupResult {
  reference: string;
  matches: CareCardRow[];
  known: { wallet: boolean; roster: boolean };
}

/** `operator-bff:GET /v1/disruptions/:urn/care@v1` — degrade-open, never a 500. */
export interface CareBoard {
  disruptionUrn: string;
  cards: CareCardRow[];
  facts: CareFacts;
  /** Honesty flags: `wallet:false` = balances were NOT read (render "balances
   *  not read", never $0.00); `roster:false` = the entitled set itself is a floor. */
  known: { wallet: boolean; roster: boolean };
  source: 'wallet' | 'fixtures';
  /** Absent only on boards served by a BFF that predates the K2 knobs read. */
  knobs?: CareKnobs;
}

/** `POST …/pnrs/:sub/care/top-up` body (K6 drives the dialog; the seam is K5's). */
export interface CareTopUpInput {
  caseUrn?: string;
  amount: number;
  currency: string;
  reasonCode: CareReasonCode;
  reason: string;
  waiveCap?: boolean;
}

/** Wallet top-up dispatch ack — the money settles asynchronously (202). */
export interface CareTopUpResult {
  loadUrn: string;
  dispatched: boolean;
}

/* ── transport-desk (slice T3, NEX-463) — disruption-grain trips + dispatch-cars ──
 * UI mirror of the FROZEN T2 BFF seams
 * (apps/operator-bff/src/modules/transport/transport.types.ts):
 *   GET  /v1/disruptions/{urn}/trips                   → DisruptionTripsPage
 *   POST /v1/disruptions/{urn}/transport/dispatch-cars → DispatchCarsResult
 * Realtime = WS: the trips page is seeded once and then upserted by the existing
 * `TripChanged` push (apply-event.ts) when the trip carries its disruptionUrn. */

/**
 * A trip as the disruption-grain read returns it — `TripView@v1` plus the
 * ADDITIVE mass-transport fields (shuttle seat facts). Everything extra is
 * optional so a plain case-grain `TripView` (the `TripChanged` payload) merges
 * into this list without loss.
 */
export interface DisruptionTripView extends TripView {
  /** The hotel this trip serves (seat trips of a shuttle run carry it). */
  hotelUrn?: string;
  /** `CAR` = per-passenger ride (incl. ON_DEMAND); `SHUTTLE` = a seat on a run. */
  kind?: 'CAR' | 'SHUTTLE';
  /** The shuttle run this seat belongs to. */
  run?: { urn: string; seq: number; vehicleClass: string; capacity: number; plate?: string };
  pickupPoint?: TransferPickupPoint;
  /** ISO-8601 — when the shuttle leaves the pickup point. */
  pickupTime?: string;
  boarding?: { status: 'ASSIGNED' | 'BOARDED' | 'NO_SHOW'; at?: string };
}

/** `operator-bff:GET /v1/disruptions/{urn}/trips@v1` — CAR / on-demand trips. */
export interface DisruptionTripsPage {
  trips: DisruptionTripView[];
  /** `transport` = the trips read answered (an empty list IS known). Degrade open. */
  known: { transport: boolean };
  source: 'transport' | 'fixtures';
  /** `airline-money@v2` (P5) — false ⇒ every trip `price` is null. Optional: older BFF. */
  priced?: boolean;
}

/**
 * `operator-bff:POST /v1/disruptions/{urn}/transport/dispatch-cars@v1` — the
 * below-threshold mass CAR fan-out. `skipped` = groups already served; `failed`
 * accumulates per-case errors instead of failing the whole fan-out.
 */
export interface DispatchCarsResult {
  dispatched: number;
  skipped: number;
  failed: Array<{ caseUrn: string; error: string }>;
}

/* ── transport-desk (slice T4, NEX-464) — run manifest + move-seats + add-run ────
 * UI mirror of the FROZEN T2 BFF seams
 * (apps/operator-bff/src/modules/transport/transport.types.ts):
 *   GET  /v1/transport/runs/{urn}/manifest       → RunManifest (URNs only, no names)
 *   POST /v1/transport/runs/{urn}/move-seats     → 200 {seats, runs} · 409 run_departed|over_capacity
 *   POST /v1/disruptions/{urn}/transfers/add-run → 201 {run} · 404 plan_not_found
 * Realtime = WS: an open run sheet's manifest is seeded once and re-read when the
 * run's own `TransferRunChanged` push lands (apply-event) — never a poll. */

/** `SeatView` — one passenger's seat on a run's manifest. URNs ONLY, never a name:
 *  the UI joins the roster walk client-side so PII stays mask-by-default. */
export interface TransferSeatView {
  passengerUrn: string;
  caseUrn: string;
  subCaseUrn: string;
  groupId?: string;
  hotelUrn: string;
  leg: TransferLeg;
  status: 'PENDING' | 'ASSIGNED' | 'BOARDED' | 'ARRIVED' | 'NO_SHOW' | 'OWN_MEANS' | string;
  runUrn?: string;
  tripUrn?: string;
  departureAt?: string;
}

/** `operator-bff:GET /v1/transport/runs/{urn}/manifest@v1`. */
export interface RunManifest {
  seats: TransferSeatView[];
}

/** `operator-bff:POST /v1/transport/runs/{urn}/move-seats@v1` body (`:urn` = the FROM run). */
export interface MoveSeatsInput {
  passengerUrns: string[];
  toRunUrn: string;
  reason: string;
}

/** 200 body of move-seats — the moved seats + every run the move touched. */
export interface MoveSeatsResult {
  seats: TransferSeatView[];
  runs: ShuttleRunView[];
}

/** `operator-bff:POST /v1/disruptions/{urn}/transfers/add-run@v1` body. */
export interface AddRunInput {
  hotelUrn: string;
  leg: TransferLeg;
  vehicleClass: string;
  /** Dispatch the new run immediately (domain-side) instead of leaving it PLANNED. */
  dispatchNow?: boolean;
  reason: string;
}

/** 201 body of add-run. */
export interface AddRunResult {
  run: ShuttleRunView;
}

/** `operator-bff:POST /v1/disruptions/{urn}/pnrs/{subCaseUrn}/transport-outcome@v1`
 *  body (T2b) — the SEATLESS own-means declaration: the group covers the ground
 *  leg themselves, no seat or trip involved. Only OWN_MEANS today. */
export interface TransportOutcomeInput {
  caseUrn?: string;
  outcome: 'OWN_MEANS';
  note?: string;
}

/** 202 body — the cases ack. */
export interface TransportOutcomeResult {
  declared: true;
  subCaseUrn: string;
  outcome: string;
}

/* ── transport-desk (slice T7, NEX-467) — the check-in loop ──────────────────────
 * UI mirror of the FROZEN T2b BFF seams
 * (apps/operator-bff/src/modules/transport/transport.types.ts):
 *   POST /v1/disruptions/{urn}/pnrs/{subCaseUrn}/passengers/{paxUrn}/check-in → 202
 *   POST /v1/disruptions/{urn}/transport/checkin-reminders/sweep → {claimed, sent, failed}
 * The actor is ALWAYS the verified operator (the BFF stamps `source:'OPERATOR'` +
 * `actorUrn` server-side — the client never names the actor). */

/** Body of the operator [Mark checked in] write. */
export interface OperatorCheckInInput {
  caseUrn: string;
  note?: string;
}

/** 202 body — the cases K3 ack (fixture parity; live passthrough may carry more). */
export interface OperatorCheckInResult {
  accepted: true;
  subCaseUrn: string;
  passengerUrn: string;
  source: string;
}

/* ── #806 — accommodation condition (minors rule, SKY PoC §4.4) ──────────────────
 * UI mirror of the BFF seams
 *   POST /v1/disruptions/{urn}/pnrs/{subCaseUrn}/passengers/{paxUrn}/condition → 202
 *   POST /v1/disruptions/{urn}/pnrs/{subCaseUrn}/passengers/{paxUrn}/responsible-adult → 202
 * The actor is ALWAYS the verified operator (stamped server-side). */

export type PassengerCondition = 'ADULT' | 'ACCOMPANIED_MINOR' | 'UNACCOMPANIED_MINOR' | 'UNKNOWN';

/** The conditions a desk may SET (never `UNKNOWN` — that is what it resolves). */
export const SETTABLE_CONDITIONS: readonly PassengerCondition[] = ['ADULT', 'ACCOMPANIED_MINOR', 'UNACCOMPANIED_MINOR'];

export interface ResponsibleAdultRef {
  pnrLocator: string;
  pnrUrn?: string;
  paxUrn?: string;
  /** Masked like every roster name. */
  name?: string;
  source?: 'MANIFEST' | 'OPERATOR';
  setBy?: string;
  setAt?: string;
}

export interface OperatorSetConditionInput {
  caseUrn: string;
  condition: PassengerCondition;
  note?: string;
}

export interface OperatorSetConditionResult {
  set: true;
  subCaseUrn: string;
  paxUrn: string;
  condition: PassengerCondition;
  previous?: PassengerCondition;
}

export interface OperatorLinkResponsibleAdultInput {
  caseUrn: string;
  pnrLocator: string;
  paxUrn?: string;
  name?: string;
}

export interface OperatorLinkResponsibleAdultResult {
  linked: true;
  subCaseUrn: string;
  paxUrn: string;
  responsibleAdult: ResponsibleAdultRef;
  /** Whether the locator matched a live PNR of the same case. */
  resolved: boolean;
}

/** `operator-bff:POST /v1/disruptions/{urn}/transport/checkin-reminders/sweep@v1`. */
export interface CheckinReminderSweepResult {
  /** Rows atomically claimed as reminder-due this sweep. */
  claimed: number;
  /** Reminder notifications actually minted (≤ claimed). */
  sent: number;
  /** Claims whose mint threw (retry-safe upstream). */
  failed: number;
}

/**
 * A transport-desk write refused by the seam with a CODED body (`{error}`):
 * move-seats 409 `run_departed` | `over_capacity`, add-run 404 `plan_not_found`.
 * Typed so dialogs can branch on `code` synchronously instead of re-reading the
 * ky response body; any non-coded failure rethrows as the plain HTTPError.
 */
export class TransportSeamError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'TransportSeamError';
  }
}

export function isTransportSeamError(e: unknown): e is TransportSeamError {
  return e instanceof TransportSeamError;
}

/* ── Express Mode — the foolproof single-flight declare ─────────────────────────
 * UI mirror of the operator-bff `POST /v1/disruptions/express` seam
 * (apps/operator-bff/src/modules/disruptions/disruptions.types.ts). Kept
 * byte-compatible; do NOT import from operator-bff (no cross-app compile dep). */

/** Body of POST /v1/disruptions/express — one flight, one button. */
export interface ExpressDeclareInput {
  flightUrn: string;
  /** Friendly title ("LA593 SCL→GRU") shown on the disruption everywhere else. */
  title?: string;
  /** Declared cause; the BFF defaults OTHER when absent. */
  cause?: DisruptionCause;
  /**
   * Care-only scope: the case is minted with flight rebooking disabled (hotels/
   * meals/transport still run). OMITTED on full handling — same wire-compat rule
   * as POST /v1/cases (`rebookingDisabled` never rides as `false`).
   */
  rebookingDisabled?: boolean;
}

/**
 * Result of the express declare. `created:true` → a fresh disruption + case.
 * `created:false` → the flight already had an open case: `disruptionUrn` is the
 * EXISTING parent to attach the board to (absent when the pre-existing case is
 * un-parented — `existingCaseUrn` then links the operator to /cases/:urn).
 */
export interface ExpressDeclareResult {
  disruptionUrn?: string;
  caseUrn?: string;
  created: boolean;
  existingCaseUrn?: string;
}

/* ── #819 (SKY PoC A9) — event-level benefit override ───────────────────────────
 * UI mirror of `cases:/internal/v1/disruptions/:urn/benefit-overrides@v1` as the
 * BFF passes it through. The policy is NEVER modified: `proposedByPolicy` is what
 * the policy stamped on this event's groups, `override` is the value one operator
 * proposed and a DIFFERENT authorizer approved. */

export type BenefitOverrideStatus = 'PROPOSED' | 'APPROVED' | 'REJECTED' | 'APPLIED';
export type BenefitOverrideApplicationOutcome = 'ISSUED' | 'TOPPED_UP' | 'ADDITIONAL' | 'SKIPPED';

export interface BenefitOverrideActor {
  urn: string;
  name?: string;
  at: string;
  note?: string;
}

export interface BenefitOverrideApplicationRow {
  subCaseUrn: string;
  pnrLabel: string;
  outcome: BenefitOverrideApplicationOutcome;
  amountUsd: number;
  linkedCardUrn?: string;
  note?: string;
}

export interface BenefitOverride {
  urn: string;
  disruptionUrn: string;
  benefit: 'MEAL_ALLOWANCE';
  policyUrn?: string;
  policyVersion?: string;
  proposedByPolicy: { amountUsd: number; currency: string };
  override: { amountUsd: number; currency: string };
  reason: string;
  status: BenefitOverrideStatus;
  proposedBy: BenefitOverrideActor;
  decidedBy?: BenefitOverrideActor;
  appliedAt?: string;
  appliedBy?: BenefitOverrideActor;
  application?: BenefitOverrideApplicationRow[];
  createdAt: string;
  updatedAt: string;
}

/** `POST /v1/disruptions/:urn/benefit-overrides` body — the BFF derives the
 *  policy-proposed value from the roster; the client's copy is only a fallback. */
export interface ProposeBenefitOverrideInput {
  amountUsd: number;
  currency?: string;
  reason: string;
  proposedByPolicy?: { amountUsd: number; currency: string };
  policyUrn?: string;
}

export interface ApplyBenefitOverrideResult {
  override: BenefitOverride;
  alreadyApplied: boolean;
  summary: Record<BenefitOverrideApplicationOutcome, number>;
}

/* ── care-card history (2026-10-30) — `GET …/pnrs/:sub/care/history` + `GET …/care/loads` ──
 * Every load and adjustment on a PNR's card: when, how much (MINOR units, the passenger's
 * face value — commercial-contract D15), who (Nexa or the operator), why (the policy tier
 * and its rule, or the operator's reason). Mirrors operator-bff `care.types.ts`. */

export type CareHistoryKind =
  | 'INITIAL_LOAD'
  | 'TOP_UP'
  | 'SCHEDULED_LOAD'
  | 'DEBIT_ADJUSTMENT'
  | 'SPEND'
  | 'REFUND'
  | 'REDEMPTION'
  | 'VOID';

export type CareHistoryOrigin = 'OPERATOR' | 'MEAL_TIER' | 'CASE_ALLOWANCE' | 'ISSUER' | 'SYSTEM' | 'UNKNOWN';

export interface CareHistoryActor {
  kind: 'NEXA' | 'OPERATOR' | 'ISSUER' | 'UNKNOWN';
  urn?: string;
  name?: string;
}

export interface CareHistoryRule {
  tierIndex: number;
  perPaxMinor: number;
  pax: number;
  delayMinutes: number;
  thresholdMinutes: number;
  policyUrn?: string;
  policyName?: string;
  /**
   * How cases issued the tier (meal timing, 2026-10-30): on the real wait, at the scheduled
   * time, forced by a supervisor, when the flight left, or with the declaration. Absent on
   * older loads.
   */
  issuedOn?: 'REAL_WAIT' | 'AT_STD' | 'FORCED' | 'ON_DEPARTURE' | 'ON_DECLARATION';
  issuedAt?: string;
  /** A forced issue: when, who and why. */
  forced?: { on?: string; by?: { urn?: string; name?: string }; reason?: string };
}

export interface CareHistoryEntry {
  id: string;
  kind: CareHistoryKind;
  at: string;
  requestedAt?: string;
  amountMinor?: number;
  direction?: 'CREDIT' | 'DEBIT';
  /** `SKIPPED`: the case allowance was already on the card — this path moved nothing. */
  status: 'SETTLED' | 'PENDING' | 'FAILED' | 'SKIPPED';
  alreadyLoadedAt?: string;
  /** A case-allowance load that repeated one already on the card (before 2026-10-30). */
  duplicateOfAt?: string;
  origin: CareHistoryOrigin;
  actor: CareHistoryActor;
  reasonCode?: string;
  note?: string;
  rule?: CareHistoryRule;
  merchant?: string;
  failureCode?: string;
}

export interface CareCardHistory {
  subCaseUrn: string;
  known: boolean;
  source: 'wallet' | 'fixtures';
  card?: {
    urn: string;
    currency: string;
    status: string;
    issuedAt?: string;
    loadedMinor: number;
    availableMinor: number;
  };
  /** Newest first. */
  entries: CareHistoryEntry[];
}

export interface CareLoadGroup {
  key: string;
  origin: 'OPERATOR' | 'MEAL_TIER' | 'CASE_ALLOWANCE' | 'OTHER';
  tierIndex?: number;
  currency: string;
  cards: number;
  settled: number;
  pending: number;
  failed: number;
  pax: number;
  totalMinor: number;
  perPaxMinor?: number;
  /** Cards that got the case allowance twice (before 2026-10-30), and how much moved twice. */
  duplicateCards?: number;
  duplicateMinor?: number;
  /** The PNR groups (sub-case URNs) the batch reached; cards a forced tier issue loaded. */
  pnrs?: string[];
  forcedCards?: number;
  firstAt: string;
  lastAt: string;
  reasonCode?: string;
  note?: string;
  actor: CareHistoryActor;
}

export interface CareLoads {
  disruptionUrn: string;
  known: boolean;
  source: 'wallet' | 'fixtures';
  /** Newest first. */
  groups: CareLoadGroup[];
}

/* ── `GET …/care/money` — what the disruption gave its passengers (2026-10-30) ── */

export interface CareMoneyKindTotal {
  kind: 'MEAL_TIER' | 'CASE_ALLOWANCE' | 'EXTRA' | 'OTHER';
  tierIndex?: number;
  cards: number;
  loadedMinor: number;
  pendingMinor: number;
  failedCards: number;
  forcedCards?: number;
  duplicateCards?: number;
  duplicateMinor?: number;
}

export interface CareMoneyCurrency {
  currency: string;
  byKind: CareMoneyKindTotal[];
  loadedMinor: number;
  pendingMinor: number;
  spentMinor: number;
  adjustedMinor: number;
  redeemedCards: number;
  redeemedMinor: number;
  voidedCards: number;
  availableMinor: number;
}

export interface CareMoneyPnr {
  subCaseUrn: string;
  caseUrn: string;
  pnrLabel: string;
  pax: number;
  cardUrn?: string;
  cardStatus?: string;
  currency?: string;
  loadedMinor: number;
  mealTiersMinor: number;
  allowanceMinor: number;
  extraMinor: number;
  otherMinor: number;
  spentMinor: number;
  availableMinor?: number;
  redeemed: boolean;
  voided: boolean;
  failed: number;
  duplicateMinor: number;
  nothingLoaded: boolean;
  lastAt?: string;
}

export interface CareMoney {
  disruptionUrn: string;
  generatedAt: string;
  known: boolean;
  source: 'wallet' | 'fixtures';
  counts: { pnrs: number; pax: number; cards: number; nothingLoaded: number; withFailures: number; loadedTwice: number };
  currencies: CareMoneyCurrency[];
  batches: CareLoadGroup[];
  pnrs: CareMoneyPnr[];
}
